// Warsaw, "First snow on Castle Square": a late-November night, the first snow falling on the wet granite of Plac
// Zamkowy. The viewer stands at the top of Krakowskie Przedmieście by St Anne's Church (52.2468 N, 21.0134 E) and looks
// north to north-east (bearing about 15°). Left to right, as seen from there: the tall tenements on the square's west
// side (the nearest with a café on its ground floor, the next under a restoration scaffold, one with a corner turret
// under a green helmet), St John's Archcathedral's stepped brick gable over their roofs (bearing about -7°), Sigismund's
// Column on its stepped pedestal (due north, about 50 m), the small far houses at the square's north apex where
// Świętojańska leaves it, the Royal Castle's north corner tower (20°), the Clock Tower in the middle of the west front
// (28°), the west front foreshortened down to the south-west corner tower (45°) and the south front running off to the
// right (east). The Palace of Culture stands behind the viewer, and the Vistula lies east below the escarpment, out of
// sight; the W-Z route runs in its tunnel under the square. The column is drawn a little nearer than it stands so its
// cross is the frame's highest point, level with the Clock Tower's spire, as in the postcards.
// Back to front: deep winter blue sky, stars, a big moon -> the far houses at the apex, bare trees, the cathedral gable
// -> the Royal Castle (brick-red, floodlit orange from the ground, the Clock Tower's clock stage glowing gold, green
// copper helmets) -> the west-side tenements -> Sigismund's Column -> the wet square mirroring the castle -> the café on
// the left (the cozy corner) and the castle lamp on the right -> snow at three depths.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, glyph, FONT3, reflect, streak, makePitch, gradeAd, hands } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const HOR = 184;                    // eye level: every standing head near the viewer is about here
const FEET = 205;                   // the near people stand on this row (above the bottom 63 rows)
const MOON = { x: 438, y: 34, r: 7 };
// Sigismund's Column (1644; granite shaft of 1949, bronze king 2.75 m, 22 m in all). Real ratios, measured on a
// frontal photo at 80 px/m: height to shaft diameter 23.5; steps and pedestal 0.30 of the height; capital, impost
// block, statue and cross 0.34. Here: 164 px high (rows 35..198), shaft 7 px, pedestal 49, top group 56.
const COL = { x: 198, tip: 35, ground: 199, shaftTop: 92, shaftBot: 150 };
// The Royal Castle's west front, seen along its length: straight cornice and base lines converging to the north.
// XN the north corner, XS the south-west corner; the Clock Tower stands in the middle of the front (u = 0.5), which
// perspective puts at 38 % of the way from XN to XS.
const CAS = { xn: 258, xs: 405, baseN: 188, baseS: 192, corN: 152, corS: 127, ridgeN: 136, ridgeS: 98, xe: 486, baseE: 191, corE: 133, ridgeE: 104 };
// The Clock Tower (Sigismund's Tower, 60 m). Real: height to front width 5.8; helmet, lantern and spire 0.31 of the
// height; the front's cornice at 0.29. Here: 151 px (rows 38..188), front 26 px, crown 47 px, cornice 44 px up.
const CT = { x0: 301, x1: 326, tip: 38, base: 188, cor: 144 };
const SWT = { x0: 397, x1: 413, tip: 74 };            // the south-west corner tower over the corner
const NT = { x0: 253, x1: 263, tip: 117 };            // the north corner tower, far off
const BB = { x: 54, y: 114, w: 52, h: 20 };           // the scaffold wrap on the second tenement: the billboard's face
const LAMP = { x: 452, y: 150, base: 262 };           // the castle lamp near the viewer (right), its top lantern
const CAFE = { x0: 0, x1: 60, floor: 208 };           // the café on the nearest tenement's ground floor (left)

/* ================= Pixel helpers ================= */
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const get = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  const fill = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) put(i, y, col); };
  return { c, x, put, get, fill, done: () => { x.putImageData(img, 0, 0); return c; } };
}
function bres(put, x0, y0, x1, y1, col) {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    put(x0, y0, col);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function mixHex(a, b, t) {
  const A = hex(a), B = hex(b), f = k => Math.round(lerp(A[k], B[k], t)).toString(16).padStart(2, '0');
  return `#${f(0)}${f(1)}${f(2)}`;
}
function ramp(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f); if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}

/* ================= Sign font: FONT3 with wide M, W, N, K, V and Polish letters ================= */
// The 3 px K and W read as H at 1x; Polish words keep their marks (Ą's ogonek and Ę's hang a row under the baseline,
// as core's Ç does), so every sign spells a real word.
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  V: ['10001', '10001', '01010', '01010', '00100'],
  N: ['1001', '1101', '1011', '1001', '1001'], K: ['1001', '1010', '1100', '1010', '1001'],
  'Ą': ['010', '101', '111', '101', '101', '001'], 'Ę': ['111', '100', '110', '100', '111', '001'],
  'Ó': ['001', '010', '101', '101', '010'], 'Ś': ['001', '011', '100', '011', '110'], 'Ć': ['001', '011', '100', '100', '011'],
  'Ł': ['100', '110', '100', '100', '111'], 'Ż': ['010', '000', '111', '010', '111'], 'Ź': ['001', '000', '111', '010', '111'],
  'Ń': ['0010', '1101', '1011', '1001', '1001'],
};
const glyphOf = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = s => [...s.toUpperCase()].reduce((w, ch) => w + (glyphOf(ch)?.[0].length ?? 3) + 1, 0) - (s ? 1 : 0);
function sign(c, s, x, y, col) {
  let cx = Math.round(x);
  for (const ch of s.toUpperCase()) { const g = glyphOf(ch); if (g) glyph(c, g, cx, Math.round(y), col); cx += (g?.[0].length ?? 3) + 1; }
  return cx - x - 1;
}

/* ================= Sky: deep winter blue, a warm glow of the Old Town's lamps low over the roofs ================= */
const SKY = ['#04061a', '#060920', '#080c27', '#0b102e', '#0e1436', '#12183e', '#171d46', '#1d224c', '#252752',
  '#2e2c56', '#393257', '#463857', '#553f56'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 190), 1.5) * 0.84;
    v += Math.max(0, 1 - Math.hypot((i - 330) / 220, (y - 190) / 80)) * 0.16;     // the floodlit castle's glow
    v += Math.max(0, 1 - Math.hypot((i - 90) / 160, (y - 190) / 70)) * 0.1;       // the café side
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 70, (y - MOON.y) / 50)) * 0.12; // moonlit sky round the moon
    return v;
  });
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  const maria = [[-2, -1, 1.8], [1, 2, 1.6], [2, -3, 1.1], [-2, 3, 0.9], [3, 1, 0.8]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -5 ? '#fffbea' : '#f2ecd8';
    if (dd > r - 1.1) col = dx + dy > 0 ? '#c4bca4' : '#e2dac4';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = dd > r - 1.1 ? '#bab29c' : '#d4ccb6';
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(52);
  while (STARS.length < 120) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 100);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 18) continue;
    STARS.push({ x, y, k: 12 + Math.floor(r() * 50), ph: r(), b: y < 50 ? 0.45 + r() * 0.5 : 0.25 + r() * 0.35, big: r() < 0.08 });
  }
}
// thin snow-cloud wisps lit by the moon (no outlines, dithered into the sky), tileable so they drift
const WISPS = [{ seed: 4, y: 6, h: 16, n: 2, k: 2 }, { seed: 8, y: 34, h: 16, n: 2, k: 3 }, { seed: 12, y: 62, h: 16, n: 2, k: 2 }, { seed: 19, y: 96, h: 16, n: 2, k: 3 }];
function renderWisp(def) {
  const r = rng(def.seed), h = def.h, blobs = [];
  for (let b = 0; b < def.n; b++) {
    const cx = r() * W, len = 50 + r() * 70;
    for (let k = 0; k < 6; k++) blobs.push({ x: cx + (r() - 0.5) * len, y: h * (0.4 + r() * 0.25), rx: 12 + r() * 18, ry: 2.2 + r() * 2 });
  }
  const p = painter(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) {
      let dx = x - b.x; dx -= Math.round(dx / W) * W;
      v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry));
    }
    if (v > 0.55) p.put(x, y, v > 0.8 ? '#262c5a' : '#1e2552');
    else if (v > 0.2 && (v - 0.2) * 2.6 > bayer(x, y)) p.put(x, y, '#181f4a');
  }
  return { c: p.done(), y: def.y, k: def.k };
}

// a low, broken mist lying over the Old Town's roofs, lit from under by the city, drifting east and breathing
// (tileable; two layers at different speeds)
function renderMist(seed, h) {
  const p = painter(W, h), r = rng(seed), blobs = [];
  for (let k = 0; k < 7; k++) blobs.push({ x: r() * W, y: h * (0.35 + r() * 0.4), rx: 28 + r() * 40, ry: 5 + r() * 6 });
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) { let dx = x - b.x; dx -= Math.round(dx / W) * W; v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry)); }
    if (v > 0.12 && v * 0.8 > bayer(x, y) + 0.05) p.put(x, y, v > 0.55 ? '#3a3a64' : '#2c2e58');
  }
  return p.done();
}

/* ================= The square's north apex: small far houses, bare trees, St John's gable ================= */
const NIGHT = '#151a3c';
const FAC = [                                                  // Old Town facade ramps, lamplit to shadowed
  ['#e2b060', '#c49040', '#94682c'], ['#9cc08a', '#7aa06c', '#557a4e'], ['#e4927c', '#c4705e', '#94503e'],
  ['#ecdcb4', '#cabb94', '#968a6e'], ['#e0a0a4', '#be7e84', '#8e5a62'], ['#a8b4cc', '#8494ae', '#5e6c86'],
  ['#e8c88c', '#c8a86c', '#94784a'], ['#d8b8d0', '#b496ae', '#866a80'],
];
const ROOFS = ['#9a4a34', '#743626', '#4e261e'];
const WARMW = ['#ffe0a0', '#f8c46e', '#e8a050'];
const SNOW = ['#eef2ff', '#ccd4f0', '#a4acd0', '#7c84aa'];
// a far tenement: tall narrow front, steep tiled roof with a snow line, rows of windows (some lit)
function drawFarHouse(p, x, w, top, base, f, dk, seed) {
  const F = FAC[f].map(c => mixHex(c, NIGHT, dk)), win = WARMW.map(c => mixHex(c, '#3a2030', dk * 0.4)), dark = mixHex('#1c1a30', NIGHT, dk);
  for (let y = top; y < base; y++) for (let i = x; i < x + w; i++) {
    const v = (base - y) / (base - top) * 0.6 + (bayer(i, y) - 0.5) * 0.2;
    p.put(i, y, i === x ? F[0] : i === x + w - 1 ? F[2] : y === top ? mixHex('#f0e6cc', NIGHT, dk) : v < 0.35 ? F[0] : v < 0.7 ? F[1] : F[2]);
  }
  const rh = Math.max(3, Math.round(w * 0.45));                 // steep roof
  for (let k = 1; k <= rh; k++) for (let i = x + Math.floor(k * 0.4); i <= x + w - 1 - Math.floor(k * 0.4); i++)
    p.put(i, top - k, k > rh - 2 || hash(i, k, seed) < 0.3 ? mixHex(SNOW[1], NIGHT, dk + 0.15) : mixHex(ROOFS[k & 1 ? 1 : 2], NIGHT, dk));
  for (let y = top + 2; y < base - 3; y += 4) for (let i = x + 1; i < x + w - 1; i += 2)
  {
    const lit = hash(seed, i, y) < 0.45;
    p.put(i, y, lit ? win[Math.floor(hash(seed, i, y + 1) * 3)] : dark);
    if (!lit && hash(seed, i, y + 7) < 0.3) WINS.push({ id: WINS.length + 300, x: i, y, w: 1, h: 2, on: win[1], top: win[0], n: 8 + Math.floor(hash(seed, i, y + 8) * 9), ph: hash(seed, i, y + 9) });
  }
  for (let i = x + 1; i < x + w - 1; i++) if ((i - x) % 3 !== 0) p.put(i, base - 2, hash(seed, i) < 0.6 ? win[1] : win[2]);   // lit shopfronts
}
function renderFar() {
  const p = painter(W, H), put = p.put;
  // St John's Archcathedral: the long roof running back, the west gable in Vistulan Gothic stepped up to a central
  // pinnacle, brick with white blind arcades, softly floodlit
  const G = { x: 160, top: 108, hw: 15, base: 150 };
  for (let y = 124; y < G.base; y++) for (let i = G.x - 4; i <= G.x + 16; i++) {     // the nave roof behind, snow on it
    const ry = 124 + (i - G.x) * 0.08;
    if (y < ry) continue;
    put(i, y, (i + y) % 5 === 0 ? '#2a2a46' : y < ry + 3 ? mixHex(SNOW[2], NIGHT, 0.35) : bayer(i, y) < 0.25 ? '#4a4a6a' : '#363654');
  }
  const steps = [[15, 136], [12, 130], [9, 124], [6, 118], [3, 113]];               // the gable's steps: half-width, top row
  for (let y = G.top + 4; y < G.base; y++) for (let i = G.x - G.hw; i <= G.x + G.hw; i++) {
    const d = Math.abs(i - G.x);
    let top = G.base; for (const [hw, ty] of steps) if (d <= hw) top = ty;             // the narrowest step over d
    if (y < top) continue;
    const recess = (d % 6 === 2 || d % 6 === 3) && y > top + 2 && y < G.base - 2;
    let col = recess ? (y < top + 4 ? '#e8dcc8' : '#d0c4b0') : i < G.x ? '#9a4432' : '#7c3428';
    if (!recess && bayer(i, y) < 0.15) col = '#6a2c24';
    if (y === top) col = '#c46a50';
    put(i, y, col);
  }
  for (const [hw, ty] of steps) for (const s of [-1, 1]) {                         // pinnacles on every step
    const px0 = G.x + s * (hw - 1);
    for (let y = ty - 4; y < ty; y++) put(px0, y, y === ty - 4 ? '#e8b090' : '#a84a36');
  }
  for (let y = G.top - 4; y < G.top + 4; y++) { put(G.x, y, y < G.top ? '#e8b090' : '#b05038'); if (y > G.top) put(G.x - 1, y, '#9a4432'); }
  for (const [i, y] of [[0, -1], [-1, 0], [1, 0], [0, 1], [0, 0]]) put(G.x + i, 128 + y, i || y ? '#e8c27a' : '#f6dca0');   // rose window
  // the far houses at the apex, behind the column, where Świętojańska leaves the square
  const r = rng(7);
  let x = 166, k = 0;
  while (x < 256) {
    const w = 8 + Math.floor(r() * 6), top = 151 + Math.floor(r() * 9);
    drawFarHouse(p, x, w, top, 188, (k * 3 + 1) % FAC.length, 0.42, 30 + k);
    x += w; k++;
  }
  for (const cx of [213, 240]) for (let y = 144; y < 152; y++) { put(cx, y, y === 144 ? SNOW[1] : '#4a3a3e'); put(cx + 1, y, y === 144 ? SNOW[1] : '#3a2e34'); }   // chimneys on the far roofs
  // bare trees by the castle's north end: trunks and a fan of thin branches
  for (const [tx, th] of [[236, 40], [246, 34], [229, 30]]) {
    const ty = 188 - th, rr = rng(tx);
    for (let y = ty + th * 0.4; y < 188; y++) put(tx, y, '#1e1a26');
    for (let b = 0; b < 9; b++) {
      const a = -Math.PI / 2 + (rr() - 0.5) * 2.2, len = th * (0.35 + rr() * 0.4), sy = ty + th * (0.3 + rr() * 0.3);
      for (let s = 0; s < len; s++) put(tx + Math.cos(a) * s * 0.8, sy + Math.sin(a) * s, s > len * 0.7 ? '#2e2a3c' : '#24202e');
    }
  }
  return p.done();
}

/* ================= The Royal Castle: brick-red, floodlit orange from the ground, copper-green helmets ================= */
const CA = { hi: '#f6b070', lt: '#e08a52', md: '#c46c40', sh: '#9a5034', dk: '#6a3628', deep: '#4a2622' };
const STONE = { hi: '#fff0d4', lt: '#ecd2a8', md: '#c8aa82', sh: '#94785e' };
const TILE = ['#6a2e28', '#56241f', '#421c1c', '#2e1418'];
const COPPER = { hi: '#6ab49a', lt: '#3e8a74', md: '#2c6656', sh: '#1e4840', dk: '#14302c' };
const GILT = ['#fff2b0', '#f0c858', '#b8862c'];
const persp = u => u / (u + 1.63 * (1 - u));                    // where a point u along the west front shows (0..1)
const sAt = x => clamp((x - CAS.xn) / (CAS.xs - CAS.xn), 0, 1);
const baseW = x => Math.round(lerp(CAS.baseN, CAS.baseS, sAt(x)));
const corW = x => Math.round(lerp(CAS.corN, CAS.corS, sAt(x)));
const ridgeW = x => Math.round(lerp(CAS.ridgeN, CAS.ridgeS, sAt(x)));
const eAt = x => clamp((x - CAS.xs) / (CAS.xe - CAS.xs), 0, 1);
const baseE = x => Math.round(lerp(CAS.baseS, CAS.baseE, eAt(x)));
const corE = x => Math.round(lerp(CAS.corS, CAS.corE, eAt(x)));
const ridgeE = x => Math.round(lerp(CAS.ridgeS, CAS.ridgeE, eAt(x)));
// window axes of the west front (19, the Clock Tower on the middle one) and of the south front
const AXES_W = Array.from({ length: 19 }, (_, k) => Math.round(CAS.xn + (CAS.xs - CAS.xn) * persp((k + 0.5) / 19)));
const AXES_E = Array.from({ length: 9 }, (_, k) => Math.round(CAS.xs + 7 + k * 9.6 - k * k * 0.12));
// a front between x0 and x1: plaster lit from the uplights along its foot, three storeys of white-framed windows
function drawFront(p, x0, x1, base, cor, axes, skip) {
  const put = p.put;
  for (let x = x0; x <= x1; x++) {
    const b = base(x), c = cor(x), h = b - c;
    for (let y = c; y < b; y++) {
      const f = (b - y) / h;                                      // 0 at the foot, 1 at the cornice
      const pool = axes.reduce((m, ax) => Math.max(m, 1 - Math.abs(x - ax) / (2 + h * 0.08) - f * 0.6), 0);
      const v = 0.9 - f * 0.55 + pool * 0.25 + (bayer(x, y) - 0.5) * 0.18;
      put(x, y, v > 0.85 ? CA.hi : v > 0.62 ? CA.lt : v > 0.4 ? CA.md : v > 0.22 ? CA.sh : CA.dk);
    }
    // the main cornice (lit cream from below, its shadow over it), the string course over the ground floor, the plinth
    put(x, c, STONE.sh); put(x, c + 1, STONE.lt); put(x, c + 2, STONE.md);
    const sc = Math.round(b - h * 0.36);
    put(x, sc, STONE.lt);
    put(x, b - 1, CA.deep); put(x, b - 2, STONE.md);
  }
  for (const ax of axes) {
    if (skip && skip(ax)) continue;
    const b = base(ax), c = cor(ax), h = b - c, ww = h > 52 ? 3 : 2;
    const rows = [[0.08, 0.24], [0.42, 0.66], [0.77, 0.87]];      // ground floor, piano nobile, the small top windows
    rows.forEach(([a, z], ri) => {
      const ya = Math.round(b - h * z), yb = Math.round(b - h * a);
      for (let y = ya - 1; y <= yb; y++) for (let i = ax - 1; i <= ax + ww; i++) {
        const frame = i === ax - 1 || i === ax + ww || y === ya - 1 || y === yb;
        const lit = hash(ax, ri, 3) < 0.07;
        if (!lit && y === ya && i === ax && hash(ax, ri, 9) < 0.45) CWINS.push({ id: CWINS.length + 50, x: ax, y: ya, w: ww, h: yb - ya, n: 8 + Math.floor(hash(ax, ri, 10) * 9), ph: hash(ax, ri, 11) });
        put(i, y, frame ? (y === ya - 1 || i === ax - 1 ? STONE.hi : STONE.lt) : lit ? (y < ya + 1 ? WARMW[0] : WARMW[1]) : (y === ya ? '#3a2630' : '#22161e'));
      }
      if (ri === 1 && h > 40) for (let i = ax - 1; i <= ax + ww; i++) put(i, ya - 2, STONE.md);   // pediments on the piano nobile
    });
  }
}
// a roof slope from the cornice back to the ridge: dark tile rows, the first snow lying on it, dormers
function drawRoof(p, x0, x1, cor, ridge, dormerXs) {
  const put = p.put;
  for (let x = x0; x <= x1; x++) {
    const c = cor(x), r = ridge(x);
    for (let y = r; y < c; y++) {
      const f = (y - r) / Math.max(1, c - r);                     // 0 at the ridge, 1 at the eaves
      const edge = 0.5 + 0.16 * hash(x >> 2, 0, 77) + 0.06 * Math.sin(x * 0.9);   // the snow's ragged lower edge
      let col = (y - r) % 3 === 0 ? TILE[3] : f > 0.85 ? TILE[2] : TILE[1];
      if (f < edge) col = f < 0.2 ? SNOW[1] : hash(x, y, 79) < 0.06 ? SNOW[3] : SNOW[2];
      else if (f < edge + 0.12 && bayer(x, y) < 0.35) col = SNOW[3];
      if (y === r) col = SNOW[0];
      put(x, y, col);
    }
  }
  for (const dx of dormerXs) {
    const c = cor(dx), r = ridge(dx), dy = Math.round(lerp(r, c, 0.5)), s = c - r > 22 ? 1 : 0;
    for (let y = dy - 3 - s; y <= dy + 1; y++) for (let i = dx - 2 - s; i <= dx + 2 + s; i++) {
      const top = y === dy - 3 - s;
      put(i, y, top ? SNOW[1] : Math.abs(i - dx) <= s && y > dy - 2 - s && y < dy + 1 ? '#2a1c22' : y === dy + 1 ? TILE[3] : CA.md);
    }
  }
}
// a copper helmet: a list of [row, half-width] from the top down, filled green with a lit left side and snow on its
// upper slopes; then the lantern and spire as given
function drawHelmet(p, cx, prof, snowRows = 2) {
  const put = p.put;
  for (let k = 0; k < prof.length; k++) {
    const [y, hw] = prof[k], prev = k ? prof[k - 1][1] : 0;
    for (let i = cx - hw; i <= cx + hw; i++) {
      const u = (i - (cx - hw)) / Math.max(1, 2 * hw), widening = hw > prev;
      let col = u < 0.22 ? COPPER.hi : u < 0.5 ? COPPER.lt : u < 0.8 ? COPPER.md : COPPER.sh;
      if (widening && Math.abs(i - cx) > prev - 1 && k < prof.length - 1 && snowRows) col = u < 0.6 ? SNOW[0] : SNOW[1];
      if ((i - cx + 40) % 3 === 0 && !widening && u > 0.15 && u < 0.85) col = COPPER.sh;       // ribs
      put(i, y, col);
    }
  }
}
function renderCastle() {
  const p = painter(W, H), put = p.put;
  // roofs first (the towers stand in front of them), then the fronts
  drawRoof(p, CAS.xn, CAS.xs, corW, ridgeW, AXES_W.filter((a, k) => k % 3 === 1 && Math.abs(a - (CT.x0 + CT.x1) / 2) > 16));
  drawRoof(p, CAS.xs, W - 1, corE, ridgeE, AXES_E.filter((a, k) => k % 2 === 1));
  // white chimney stacks standing out of the roofs, snow on their caps
  for (const [cx, w, h] of [[284, 2, 7], [356, 3, 9], [437, 4, 12], [468, 4, 11]]) {
    const r = cx > CAS.xs ? ridgeE(cx) : ridgeW(cx), top = r - h + 4;
    for (let y = top; y < r + 6; y++) for (let i = cx; i < cx + w; i++) put(i, y, y === top ? SNOW[0] : i === cx ? '#e8dcc8' : '#b0a494');
  }
  drawFront(p, CAS.xn, CAS.xs - 1, baseW, corW, AXES_W, ax => ax >= CT.x0 - 2 && ax <= CT.x1 + 2);
  drawFront(p, CAS.xs, W - 1, baseE, corE, AXES_E);
  // rusticated quoins on the south-west corner
  for (let y = corW(CAS.xs) + 3; y < CAS.baseS - 1; y++) {
    const blk = Math.floor((y - CAS.corS) / 4), long = blk & 1;
    for (let i = CAS.xs - (long ? 3 : 2); i <= CAS.xs + (long ? 3 : 2); i++)
      put(i, y, (y - CAS.corS) % 4 === 3 ? STONE.sh : i < CAS.xs ? STONE.lt : i === CAS.xs ? STONE.hi : STONE.md);
  }
  // ---- the Clock Tower: risalit with the gate, the shaft, the balcony, the clock stage, the copper crown ----
  const { x0, x1, base, cor } = CT, cx = (x0 + x1) >> 1;
  const band = (y0, y1, a, b, light = 0) => {
    for (let y = y0; y <= y1; y++) for (let i = a; i <= b; i++) {
      const f = (base - y) / (base - CT.tip), v = 0.95 - f * 0.5 + light + (bayer(i, y) - 0.5) * 0.18 - (i > b - 3 ? 0.25 : 0);
      put(i, y, i === a ? STONE.lt : i === b ? CA.dk : v > 0.85 ? CA.hi : v > 0.62 ? CA.lt : v > 0.42 ? CA.md : CA.sh);
    }
  };
  band(cor + 3, base - 1, x0 - 1, x1 + 1, 0.05);                       // the risalit, a step proud of the front
  for (let y = base - 15; y < base - 1; y++) for (let i = cx - 4; i <= cx + 4; i++) {   // the arched gate
    const arch = y < base - 11 && Math.hypot(i - cx, (y - (base - 11)) * 1.2) > 4.2;
    if (arch) continue;
    put(i, y, Math.abs(i - cx) === 4 || y === base - 15 ? STONE.lt : y > base - 6 ? '#3a2620' : '#2a1a1c');
  }
  for (let i = cx - 7; i <= cx + 7; i++) { put(i, base - 18, STONE.hi); put(i, base - 17, '#1e1418'); }   // the balcony over it
  for (let i = cx - 6; i <= cx + 6; i += 2) put(i, base - 19, STONE.md);
  for (let y = base - 31; y < base - 20; y++) for (let i = cx - 1; i <= cx + 1; i++) put(i, y, y === base - 31 ? STONE.hi : y === base - 30 ? '#3a2630' : '#24161c');   // the balcony door
  for (let i = cx - 2; i <= cx + 2; i++) put(i, base - 32, STONE.md);
  for (let i = x0 - 1; i <= x1 + 1; i++) { put(i, cor, STONE.sh); put(i, cor + 1, STONE.hi); put(i, cor + 2, STONE.md); }
  // the shaft over the roofs: a window and the cartouche with the eagle
  band(117, cor - 1, x0, x1);
  for (let y = 120; y < 128; y++) for (let i = cx - 3; i <= cx + 3; i++) {
    const d = Math.hypot(i - cx, (y - 124) * 0.8);
    if (Math.hypot(i - cx, (y - 124) * 1.1) < 2.8) put(i, y, d > 1.8 ? STONE.hi : STONE.md);
  }
  for (let y = 131; y < 139; y++) for (let i = cx - 1; i <= cx + 1; i++) put(i, y, y === 131 ? STONE.hi : '#2a1a20');
  for (let y = 117; y < cor; y++) { put(x0 + 1, y, STONE.lt); put(x1 - 1, y, STONE.md); }      // pilasters at its corners
  // the balcony band (lit from inside, warm), the clock stage, its cornice
  for (let y = 111; y <= 116; y++) for (let i = x0 - 1; i <= x1 + 1; i++) {
    const rail = y === 111 || y === 116;
    put(i, y, rail ? STONE.hi : (i - x0) % 3 === 0 ? STONE.md : y < 114 ? '#ffe2a0' : '#f4b860');
  }
  band(93, 110, x0, x1, 0.25);
  for (let y = 93; y <= 110; y++) { put(x0 + 1, y, STONE.hi); put(x1 - 1, y, STONE.md); }
  for (let y = 105; y <= 110; y++) for (const wx of [cx - 4, cx + 2]) for (let i = wx; i < wx + 3; i++)   // two windows under the clock, lit warm
    put(i, y, y === 105 ? STONE.hi : i === wx + 1 ? '#8a5a34' : y < 107 ? '#f6c878' : '#d8984e');
  for (let y = 0; y <= 10; y++) for (let i = 0; i <= 10; i++) {                           // the clock: a copper dial in a stone ring
    const d = Math.hypot(i - 5, y - 5);
    if (d > 5.3) continue;
    const mark = d > 3.2 && d <= 4.3 && (i === 5 || y === 5);
    put(cx - 5 + i, 93 + y, d > 4.3 ? (i + y < 10 ? GILT[1] : GILT[2]) : mark ? GILT[0] : d > 3.2 ? '#4a3a30' : '#3a2e28');
  }
  for (let y = 88; y <= 92; y++) for (let i = x0 - 2 + (92 - y > 2 ? 0 : 0); i <= x1 + 2; i++)
    put(i, y, y === 88 ? SNOW[1] : y === 89 ? STONE.hi : y === 92 ? STONE.sh : i < cx ? STONE.lt : STONE.md);
  // the crown: a bell-shaped copper helmet, the lantern, a small cupola, the spire with gilt balls and a vane
  drawHelmet(p, cx, [[68, 2], [69, 2], [70, 3], [71, 4], [72, 5], [73, 6], [74, 7], [75, 8], [76, 8], [77, 9], [78, 9],
    [79, 10], [80, 10], [81, 10], [82, 9], [83, 9], [84, 9], [85, 10], [86, 10], [87, 11]]);
  for (let y = 60; y <= 67; y++) for (let i = cx - 3; i <= cx + 3; i++)                   // the lantern: open, lit inside
    put(i, y, Math.abs(i - cx) === 3 || y === 60 ? COPPER.lt : (i - cx + 3) % 2 ? '#ffd890' : '#3a3020');
  drawHelmet(p, cx, [[55, 1], [56, 2], [57, 3], [58, 3], [59, 4]], 1);
  for (let y = 39; y <= 54; y++) put(cx, y, y > 50 ? COPPER.lt : '#3a4a44');
  for (const [by, r] of [[50, 1], [44, 1]]) for (let i = cx - r; i <= cx + r; i++) put(i, by, i < cx ? GILT[0] : GILT[1]);
  put(cx, 38, GILT[0]); put(cx + 1, 40, GILT[2]); put(cx + 2, 40, GILT[2]); put(cx + 1, 41, GILT[2]);   // the vane
  for (let y = 93; y <= 145; y++) for (let i = x1 + 2; i <= x1 + 4; i++) put(i, y, y < 117 && y > 110 ? '#b46a3c' : CA.dk);   // its south face in shade
  // ---- the south-west corner tower ----
  const sw = SWT, scx = (sw.x0 + sw.x1) >> 1;
  for (let y = 106; y < CAS.corS; y++) for (let i = sw.x0; i <= sw.x1; i++) {
    const v = 0.55 + (CAS.corS - y) / 60 + (bayer(i, y) - 0.5) * 0.2 - (i > scx ? 0.2 : 0);
    put(i, y, i === sw.x0 ? STONE.lt : i === sw.x1 ? CA.dk : i === scx ? STONE.md : v > 0.8 ? CA.lt : v > 0.5 ? CA.md : CA.sh);
  }
  for (let y = 112; y < 120; y++) for (const wx of [sw.x0 + 3, sw.x1 - 4]) for (let i = wx; i < wx + 2; i++) put(i, y, y === 112 ? STONE.hi : '#24161c');
  for (let i = sw.x0 - 1; i <= sw.x1 + 1; i++) { put(i, 104, SNOW[1]); put(i, 105, STONE.hi); put(i, 106, STONE.md); }
  drawHelmet(p, scx, [[86, 1], [87, 2], [88, 3], [89, 4], [90, 5], [91, 6], [92, 6], [93, 7], [94, 7], [95, 7], [96, 6],
    [97, 5], [98, 4], [99, 4], [100, 5], [101, 6], [102, 7], [103, 7]]);
  for (let y = 80; y <= 85; y++) for (let i = scx - 2; i <= scx + 2; i++) put(i, y, Math.abs(i - scx) === 2 || y === 80 ? COPPER.lt : (i - scx) & 1 ? '#3a3020' : '#e8b860');
  drawHelmet(p, scx, [[77, 1], [78, 2], [79, 2]], 1);
  for (let y = sw.tip; y <= 76; y++) put(scx, y, '#3a4a44');
  put(scx - 1, 76, GILT[1]); put(scx, 76, GILT[0]); put(scx + 1, 76, GILT[1]); put(scx, sw.tip, GILT[0]);
  // ---- the north corner tower, far off ----
  const nt = NT, ncx = (nt.x0 + nt.x1) >> 1;
  for (let y = 140; y < CAS.corN; y++) for (let i = nt.x0; i <= nt.x1; i++)
    put(i, y, i === nt.x0 ? STONE.md : i === nt.x1 ? CA.dk : y < 142 ? STONE.lt : (i < ncx ? CA.md : CA.sh));
  drawHelmet(p, ncx, [[126, 1], [127, 2], [128, 3], [129, 4], [130, 4], [131, 4], [132, 3], [133, 3], [134, 4], [135, 5], [136, 5], [137, 5], [138, 6], [139, 6]], 1);
  for (let y = nt.tip; y < 126; y++) put(ncx, y, '#3a4a44');
  put(ncx, 121, GILT[1]);
  return p.done();
}

/* ================= The west side of the square: tall tenements receding to the column ================= */
// Rebuilt after 1945 as they were: plastered fronts in ochre, green, salmon and cream, white window frames, steep tiled
// roofs with dormers. Each is drawn nearly front-on and scaled by its distance; their feet climb towards the horizon.
const TEN = [
  { x0: 0, x1: 56, base: 208, cor: 82, ridge: 50, f: 0, st: 22, axes: [9, 27, 45], ww: 6, dk: 0.12, seed: 3 },     // the café's
  { x0: 56, x1: 108, base: 203, cor: 108, ridge: 86, f: 3, st: 18, axes: [64, 79, 94], ww: 5, dk: 0.2, seed: 5 },  // under scaffold
  { x0: 108, x1: 146, base: 198, cor: 127, ridge: 111, f: 1, st: 14, axes: [114, 125, 136], ww: 4, dk: 0.3, seed: 9 },
  { x0: 146, x1: 178, base: 194, cor: 141, ridge: 130, f: 2, st: 11, axes: [152, 161, 170], ww: 3, dk: 0.38, seed: 13 },
];
const WINS = [];                                               // windows that switch on and off over the loop
function drawTenement(p, T, ti) {
  const put = p.put, F = FAC[T.f].map(c => mixHex(c, NIGHT, T.dk)), R = ROOFS.map(c => mixHex(c, NIGHT, T.dk + 0.08));
  const win = WARMW.map(c => mixHex(c, '#3a2030', T.dk * 0.4)), dark = mixHex('#1a1a30', NIGHT, T.dk), frame = mixHex('#efe6d2', NIGHT, T.dk + 0.1);
  const { x0, x1, base, cor, ridge, st } = T, ground = base - Math.round(st * 1.55);
  // the front: lit from the square's lamps below, darker under the eaves
  for (let y = cor; y < base; y++) for (let i = x0; i < x1; i++) {
    const v = (y - cor) / (base - cor) * 0.7 + 0.2 + (bayer(i, y) - 0.5) * 0.2;
    put(i, y, i === x1 - 1 ? F[2] : i === x0 && ti ? mixHex(F[0], '#fff0d0', 0.2) : v > 0.62 ? F[0] : v > 0.36 ? F[1] : F[2]);
  }
  for (let i = x0; i < x1; i++) { put(i, cor, frame); put(i, cor + 1, F[2]); put(i, ground, frame); }
  // the steep roof, snow lying on it, a dormer or two and a chimney
  for (let y = ridge; y < cor; y++) {
    const inset = Math.round((cor - y) * 0.18);
    for (let i = x0 + inset; i < x1 - inset; i++) {
      const f = (y - ridge) / (cor - ridge), edge = 0.55 + 0.18 * hash(i >> 2, ti, 5) + 0.06 * Math.sin(i * 1.1);
      let col = (y - ridge) % 3 === 0 ? R[2] : R[1];
      if (f < edge) col = f < 0.25 ? SNOW[0] : hash(i, y, 7) < 0.06 ? SNOW[2] : SNOW[1];
      else if (f < edge + 0.1 && bayer(i, y) < 0.35) col = SNOW[2];
      if (y === ridge) col = SNOW[0];
      put(i, y, col);
    }
  }
  const dorm = T.axes.filter((_, k) => k !== 1);
  for (const ax of dorm) {
    const dh = Math.max(4, Math.round(st * 0.45)), dy = cor - dh - 2, dw = T.ww + 2;
    for (let y = dy - 1; y < dy + dh; y++) for (let i = ax - 1; i < ax + dw - 1; i++) {
      const roofRow = y === dy - 1, glass = y > dy && i > ax - 1 && i < ax + dw - 2;
      put(i, y, roofRow ? SNOW[0] : glass ? (hash(T.seed, ax, 7) < 0.5 ? win[1] : dark) : frame);
    }
  }
  const chx = x0 + Math.round((x1 - x0) * 0.62);
  for (let y = ridge - Math.round(st * 0.4); y < ridge + 2; y++) for (let i = chx; i < chx + Math.max(2, T.ww - 2); i++) put(i, y, y === ridge - Math.round(st * 0.4) ? SNOW[0] : R[0]);
  // windows: storey by storey; lit or dark; a few switch over the loop (WINS)
  for (let y = cor + Math.round(st * 0.3), s = 0; y + st * 0.6 < ground; y += st, s++) {
    const wh = Math.round(st * 0.58);
    for (const ax of T.axes) {
      const r = hash(T.seed, s, ax), lit = r < 0.42, toggles = r > 0.58;
      const on = win[Math.floor(hash(T.seed, s, ax + 1) * 3)];
      for (let j = -1; j <= wh; j++) for (let k = -1; k <= T.ww; k++) {
        const fr = j === -1 || j === wh || k === -1 || k === T.ww;
        let col = fr ? frame : lit ? (j === 0 ? win[0] : on) : (j === 0 ? mixHex('#2c2a40', NIGHT, T.dk) : dark);
        if (!fr && T.ww >= 5 && k === (T.ww >> 1)) col = fr ? frame : mixHex('#d8ccb4', NIGHT, T.dk + (lit ? 0 : 0.4));   // the sash bar
        if (!fr && lit && T.ww >= 5 && (k === 0 || k === T.ww - 1) && j < wh - 1) col = mixHex('#b04a3a', NIGHT, T.dk);    // curtains
        put(ax + k, y + j, col);
      }
      if (j0Sill(T)) for (let k = -2; k <= T.ww + 1; k++) put(ax + k, y + wh + 1, SNOW[1]);   // snow on the sills
      if (toggles) WINS.push({ id: WINS.length, x: ax, y: y + 1, w: T.ww, h: wh - 1, on, top: win[0], n: 8 + Math.floor(hash(T.seed, s, ax + 3) * 9), ph: hash(T.seed, s, ax + 4) });
    }
  }
}
const j0Sill = T => T.st >= 14;
// the café on the nearest tenement's ground floor: two arched windows full of warm light, the door between, its name
function renderCafe(p) {
  const put = p.put, T = TEN[0], top = T.base - Math.round(T.st * 1.55) + 1, base = T.base;
  for (let y = top; y < base; y++) for (let i = T.x0; i < T.x1; i++) put(i, y, (i + y) % 7 === 0 ? '#5a3a2a' : y > base - 4 ? '#3a2a28' : '#6a4632');
  for (let y = top + 1; y < top + 8; y++) for (let i = T.x0 + 2; i < T.x1 - 2; i++) put(i, y, y === top + 1 || y === top + 7 ? '#c89a5a' : '#1e2a26');   // the fascia
  const arch = (a, b, y0, y1) => {
    const cx = (a + b) / 2, hw = (b - a) / 2;
    for (let y = y0; y < y1; y++) for (let i = a; i <= b; i++) {
      const inA = y >= y0 + hw ? true : Math.hypot((i - cx) / hw, (y - (y0 + hw)) / hw) <= 1;
      if (!inA) continue;
      const edge = !(y >= y0 + hw ? true : Math.hypot((i - cx) / (hw - 1), (y - (y0 + hw)) / (hw - 1)) <= 1) || i === a || i === b;
      const v = (y - y0) / (y1 - y0) + (bayer(i, y) - 0.5) * 0.25;
      put(i, y, edge ? '#e8d4a8' : v < 0.25 ? '#ffe6b0' : v < 0.6 ? '#ffc878' : '#f0a458');
    }
  };
  arch(T.x0 + 2, T.x0 + 19, top + 10, base - 3);
  arch(T.x1 - 21, T.x1 - 4, top + 10, base - 3);
  for (let y = top + 12; y < base - 2; y++) for (let i = 24; i <= 31; i++) put(i, y, i === 24 || i === 31 || y === top + 12 ? '#e8d4a8' : y < top + 16 ? '#ffd890' : '#8a4e2a');   // the door, its fanlight lit
  // inside the windows: the counter, cakes under glass, a hanging lamp, shelves
  for (const wx of [T.x0 + 4, T.x1 - 19]) {
    for (let i = wx; i < wx + 13; i++) { put(i, base - 9, '#7a4a2a'); put(i, base - 8, '#5a3420'); }
    for (let i = wx + 1; i < wx + 12; i += 3) { put(i, base - 10, '#f2e0c8'); put(i + 1, base - 10, '#d86a6a'); }
    for (let i = wx + 2; i < wx + 11; i++) put(i, top + 18, '#8a5a30');
    for (let i = wx + 3; i < wx + 10; i += 2) put(i, top + 17, hash(i, wx) < 0.5 ? '#3a6a4a' : '#c8b088');
  }
  return top;
}
function renderTown() {
  const p = painter(W, H);
  TEN.forEach((T, i) => drawTenement(p, T, i));
  // the corner turret on the green house: an oriel rising over its cornice under a green copper helmet
  const tx = 141, put = p.put;
  for (let y = 112; y < 150; y++) for (let i = tx; i <= tx + 6; i++) {
    const f = FAC[1].map(c => mixHex(c, NIGHT, 0.3));
    put(i, y, i === tx ? f[0] : i === tx + 6 ? f[2] : (y - 112) % 8 === 7 ? mixHex('#efe6d2', NIGHT, 0.4) : (y - 112) % 8 > 2 && (y - 112) % 8 < 6 && i > tx + 1 && i < tx + 5 ? (hash(i, y) < 0.5 ? '#f6c46e' : '#22203a') : f[1]);
  }
  drawHelmet(p, tx + 3, [[96, 0], [97, 1], [98, 1], [99, 2], [100, 3], [101, 3], [102, 3], [103, 2], [104, 2], [105, 3], [106, 4], [107, 4], [108, 4], [109, 4], [110, 4], [111, 4]], 1);
  for (let y = 91; y < 96; y++) put(tx + 3, y, '#3a4a44');
  put(tx + 3, 93, GILT[1]);
  const cafeTop = renderCafe(p);
  // the shopfronts of the farther houses: a lit window each
  for (const [T, word] of [[TEN[1], null], [TEN[2], null], [TEN[3], null]]) {
    const top = T.base - Math.round(T.st * 1.55) + 2;
    for (let y = top; y < T.base - 1; y++) for (let i = T.x0 + 3; i < T.x1 - 3; i++)
      if ((i - T.x0) % Math.max(6, T.st) < Math.max(4, T.st - 4)) put(i, y, y < top + 2 ? '#ffe2a0' : y < T.base - 3 ? '#f2b464' : '#8a5a34');
    void word;
  }
  const c = p.done();
  sign(p.x, 'KAWIARNIA', 12, cafeTop + 2, '#f6dca0');
  return c;
}

/* ================= Sigismund's Column ================= */
// Steps, the dark granite plinth, the die with its bronze plaque, a moulded cornice with a bronze eagle at each corner,
// the light granite shaft, the bronze Corinthian capital, the wide stone impost block, the statue's own small plinth,
// and the king in armour: sabre in his right hand, the cross raised in his left. Floodlit from the square: the shaft
// warm-white, the bronze king pale and cool where the light catches him. Snow on every ledge.
const GRAN = { hi: '#dcd2c4', lt: '#bcb0a0', md: '#9a8e80', sh: '#746a60', dk: '#504a44' };      // warm grey granite
const PED = { hi: '#8a8690', lt: '#6a6672', md: '#4e4a56', sh: '#38343e', dk: '#24222a' };
const BRZ = { hi: '#d8e8dc', lt: '#a8bcae', md: '#768a7e', sh: '#4c5a52', dk: '#2c3430' };       // the king, floodlit
const CAP = { hi: '#c8b070', lt: '#8a8452', md: '#5a5c40', sh: '#3a3e2e', dk: '#24281e' };       // the capital's dark bronze
const IMP = { hi: '#c8beb0', lt: '#a69c8e', md: '#857c70', sh: '#625a52', dk: '#443e3a' };       // the impost, a step darker
// the king: crown, armour, an ermine-trimmed cloak sweeping down his left side, the cross held upright in his left hand,
// the sabre in his right, its curved blade rising out to the side, his right foot on a plumed helmet
const KING = [                                                // rows 35..61, columns COL.x - 8 .. COL.x + 7
  '.............X..',
  '............XXX.',
  '.............X..',
  '.............X..',
  '.............X..',
  '......ggg....X..',
  '......gGg....X..',
  '......hlm....X..',
  '......hlm....X..',
  '.....ehlme...X..',
  's...hhllmdcc.X..',
  's..h.hlmmdcccH..',
  's..h.hlmmdccc...',
  '.s.h.hlmmdccc...',
  '..hh.hlmmdcccc..',
  '.....hlmmdcccc..',
  '.....hlmmmdccc..',
  '.....hlmmmdcccc.',
  '.....eeeeedcccc.',
  '......lm.mdcccc.',
  '......lm.mdcccc.',
  '......lm.md.ccc.',
  '.....hlm.md.ccc.',
  '...bbhl..md..cc.',
  '..bbbb...md.....',
  '...bbbhl.md.....',
  '....hldd.mdd....',
];
const KING_KEY = { X: '#c8ccc0', h: BRZ.hi, l: BRZ.lt, m: BRZ.md, d: BRZ.sh, c: BRZ.dk, g: '#e8d890', G: '#c84a4a', e: '#eef0e8', b: BRZ.sh, s: '#e6eef2', H: BRZ.lt };
function renderColumn() {
  const p = painter(W, H), put = p.put, cx = COL.x;
  const block = (y0, y1, hw, P, snowTop) => {
    for (let y = y0; y <= y1; y++) for (let i = cx - hw; i <= cx + hw; i++) {
      const u = (i - (cx - hw)) / (2 * hw), v = u + (bayer(i, y) - 0.5) * 0.12;
      let col = i === cx - hw ? P.hi : i === cx + hw ? P.dk : v < 0.35 ? P.lt : v < 0.75 ? P.md : P.sh;
      if (y === y0 && snowTop) col = u < 0.5 ? SNOW[0] : SNOW[1];
      put(i, y, col);
    }
  };
  // three steps, snow on their treads
  block(197, 199, 21, PED, true); block(195, 196, 18, PED, true); block(193, 194, 15, PED, true);
  // plinth, die (its bronze plaque), cornice; four bronze eagles over the cornice's corners round the shaft's base
  block(181, 192, 9, PED, false);
  for (let i = cx - 9; i <= cx + 9; i++) put(i, 181, PED.hi);
  block(160, 180, 6, { ...PED, lt: '#5a5662', md: '#46424e' }, false);
  for (let y = 164; y <= 176; y++) for (let i = cx - 4; i <= cx + 4; i++) {
    const edge = y === 164 || y === 176 || Math.abs(i - cx) === 4;
    put(i, y, edge ? '#8a7448' : (y - 166) % 2 === 0 && Math.abs(i - cx) < 3 && y < 175 ? '#a89060' : '#5a4a32');
  }
  block(157, 159, 8, PED, false); block(156, 156, 9, PED, true);
  block(152, 155, 5, GRAN, false);
  for (const s of [-1, 1]) {
    const ex = cx + s * 7;                                       // an eagle: body, spread wing, head turned out
    for (const [i, y, c] of [[0, 155, CAP.sh], [0, 154, CAP.md], [0, 153, CAP.lt], [s, 153, CAP.md], [s, 152, CAP.hi],
      [-s, 154, CAP.md], [-s * 2, 153, CAP.sh], [-s * 2, 152, CAP.lt], [-s * 3, 152, CAP.sh], [s, 151, SNOW[1]], [0, 152, SNOW[1]]]) put(ex + i, y, c);
  }
  // the shaft: warm grey granite, lit from the left front, smooth
  for (let y = COL.shaftTop; y <= COL.shaftBot; y++) for (let i = cx - 3; i <= cx + 3; i++) {
    const k = i - (cx - 3);
    put(i, y, k === 0 ? GRAN.hi : k <= 2 ? GRAN.lt : k <= 4 ? GRAN.md : k === 5 ? GRAN.sh : GRAN.dk);
  }
  for (let i = cx - 4; i <= cx + 4; i++) put(i, COL.shaftBot, i < cx ? GRAN.lt : GRAN.sh);
  // the bronze Corinthian capital: the astragal, two rows of acanthus leaves, the volutes curling out at the corners
  for (let i = cx - 4; i <= cx + 4; i++) put(i, 91, i < cx ? CAP.lt : CAP.sh);
  for (let y = 85; y <= 90; y++) {
    const hw = y > 87 ? 4 : 5, upper = y <= 87;
    for (let i = cx - hw; i <= cx + hw; i++) {
      const leaf = ((i - cx + 10 + (upper ? 1 : 0)) % 2) === 0, tip = y === (upper ? 85 : 88);
      put(i, y, leaf ? (tip ? CAP.hi : i < cx ? CAP.lt : CAP.md) : (i < cx - 2 ? CAP.md : i > cx + 2 ? CAP.dk : CAP.sh));
    }
  }
  for (let i = cx - 6; i <= cx + 6; i++) put(i, 84, Math.abs(i - cx) >= 5 ? CAP.lt : i < cx ? CAP.md : CAP.sh);
  for (const s of [-1, 1]) { put(cx + s * 7, 83, CAP.hi); put(cx + s * 7, 84, CAP.md); put(cx + s * 6, 85, CAP.sh); }
  for (let i = cx - 7; i <= cx + 7; i++) put(i, 83, Math.abs(i - cx) === 7 ? CAP.hi : i < cx ? CAP.lt : CAP.md);   // the abacus
  for (let i = cx - 6; i <= cx + 6; i++) put(i, 82, CAP.dk);
  // the square plinth over it (the impost): a block with a recessed panel under a projecting slab, snow on the slab
  for (let y = 75; y <= 81; y++) for (let i = cx - 6; i <= cx + 6; i++) {
    const k = i - (cx - 6), panel = y > 76 && y < 81 && k > 1 && k < 11;
    put(i, y, k === 0 ? IMP.hi : k === 12 ? IMP.dk : panel ? (k === 2 || y === 77 ? IMP.sh : k < 7 ? IMP.md : IMP.sh) : k < 6 ? IMP.lt : IMP.md);
  }
  for (const [y, hw, P] of [[74, 8, 'sh'], [73, 9, 'md'], [72, 10, 'lt'], [71, 10, 'hi']])
    for (let i = cx - hw; i <= cx + hw; i++) put(i, y, i === cx + hw ? IMP.dk : y === 73 && i > cx ? IMP.sh : IMP[P]);
  for (let i = cx - 10; i <= cx + 10; i++) put(i, 70, i < cx + 3 ? SNOW[0] : SNOW[1]);
  // the statue's own plinth
  for (let y = 62; y <= 69; y++) {
    const hw = y === 62 || y === 69 ? 5 : 4;
    for (let i = cx - hw; i <= cx + hw; i++) {
      const u = (i - (cx - hw)) / (2 * hw);
      put(i, y, y === 62 ? SNOW[1] : u < 0.15 ? IMP.hi : u < 0.55 ? IMP.lt : u < 0.85 ? IMP.md : IMP.sh);
    }
  }
  // the king, his sabre and his cross
  KING.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = KING_KEY[row[i]]; if (col) put(cx - 8 + i, COL.tip + j, col); } });
  return p.done();
}

/* ================= The square: wet granite setts under the first snow ================= */
// The far square lies white at a grazing angle; nearer, the snow has only settled in the joints and the setts' tops
// are wet and dark, holding the castle's orange. Trodden paths run to the column and to the castle gate, and the
// café, the castle's uplights and the lamp warm the stone round them.
const PATHS = [[[240, 270], [214, 230], [200, 200]], [[300, 270], [308, 230], [313, 190]], [[0, 207], [180, 203], [480, 204]]];
function pathDist(x, y) {
  let m = 1e9;
  for (const P of PATHS) for (let k = 1; k < P.length; k++) {
    const [ax, ay] = P[k - 1], [bx, by] = P[k], dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
    const u = clamp(((x - ax) * dx + (y - ay) * dy) / l2, 0, 1);
    m = Math.min(m, Math.hypot(x - ax - u * dx, y - ay - u * dy));
  }
  return m;
}
// the row the square's ground starts at, column by column: under each building's foot
function footY(x) {
  for (const T of TEN) if (x >= T.x0 && x < T.x1) return T.base + 1;
  if (x < CAS.xn) return 188;
  return x <= CAS.xs ? baseW(x) : baseE(x);
}
function vnoise(x, y, sc, seed) {                               // smooth value noise for the snow's patches
  const fx = x / sc, fy = y / sc, ix = Math.floor(fx), iy = Math.floor(fy), u = fx - ix, v = fy - iy;
  const s = q => q * q * (3 - 2 * q), a = hash(ix, iy, seed), b = hash(ix + 1, iy, seed), c = hash(ix, iy + 1, seed), d = hash(ix + 1, iy + 1, seed);
  return lerp(lerp(a, b, s(u)), lerp(c, d, s(u)), s(v));
}
const warmAt = (x, y) => Math.max(0, 1 - Math.hypot((x - 30) / 64, (y - 214) / 24)) +
  Math.max(0, 1 - Math.hypot((x - LAMP.x) / 64, (y - 250) / 24)) * 0.85 +
  (x > CAS.xn - 4 ? Math.max(0, 1 - (y - 192) / 16) * 0.75 : 0);
// the wet granite setts (the mirror of the frame is laid on them per frame, then the snow over that)
function renderGround() {
  const p = painter(W, H), put = p.put;
  for (let y = 186, row = 0; y < H; row++) {
    const z = y - HOR, sh = Math.max(1, Math.round(z / 8)), sw = Math.max(3, Math.round(3 + z / 3)), off = Math.floor(hash(row, 3, 17) * sw);
    for (let yy = y; yy < Math.min(H, y + sh); yy++) for (let x = 0; x < W; x++) {
      const k = Math.floor((x + off) / sw), lx = (x + off) % sw, ly = yy - y, n = hash(k, row, 17), w = warmAt(x, yy);
      const joint = lx === 0 || (ly === 0 && sh > 2 && n < 0.8);
      let v = (n - 0.5) * 0.3 + w * 0.85 + (bayer(x, yy) - 0.5) * 0.18;
      if (!joint && ly === 1 && sh > 2) v += lx < sw / 2 ? 0.32 : 0.18;               // the wet top edge of each stone
      const col = joint ? (w > 0.5 ? '#24181c' : '#111222') : v > 0.95 ? '#7a5038' : v > 0.66 ? '#563c34' : v > 0.4 ? '#3a3040' : v > 0.15 ? '#282a40' : v > -0.08 ? '#212236' : '#1a1b2e';
      put(x, yy, col);
    }
    y += sh;
  }
  return p.done();
}
// the first snow: lying white on the far square at a grazing angle, in patches nearer, drifted against every wall,
// trodden off the paths; warm where the café, the castle and the lamp light it
function renderSnowLayer() {
  const p = painter(W, H), put = p.put;
  for (let x = 0; x < W; x++) {
    const fy = footY(x);
    for (let y = Math.max(186, fy); y < H; y++) {
      const z = y - HOR, far = clamp((203 - y) / 5, 0, 1);
      const patch = vnoise(x, y * 1.7, 6 + z * 0.3, 9) * 0.9 + vnoise(x, y * 1.7, 3 + z * 0.08, 21) * 0.22;
      let amt = Math.max(far, (patch - 0.68) * 5);
      if (y - fy < 2) amt = 1;                                                       // drifted against the wall
      const pd = pathDist(x, y), tw = 2 + z * 0.12;
      if (pd < tw && y - fy >= 2) amt *= pd / tw * 0.5;
      if (amt < bayer(x, y) + 0.05) continue;
      const w = warmAt(x, y), edge = amt < 0.75;
      put(x, y, w > 0.55 ? (edge ? '#c89a7a' : '#f2caa2') : w > 0.25 ? (edge ? '#a8a0b8' : '#e4d2cc') : edge ? SNOW[2] : (y < 196 ? SNOW[1] : (hash(x, y, 3) < 0.2 ? SNOW[0] : SNOW[1])));
    }
  }
  // footprints along the paths: pairs of dark prints in the snow's edge
  for (const P of PATHS.slice(0, 2)) for (let k = 1; k < P.length; k++) {
    const [ax, ay] = P[k - 1], [bx, by] = P[k], len = Math.hypot(bx - ax, by - ay);
    for (let s = 3; s < len; s += 4 + Math.floor(hash(k, s, 8) * 4)) {
      const u = s / len, x = ax + (bx - ax) * u, y = ay + (by - ay) * u, z = y - HOR, off = 2 + z * 0.13;
      for (const sg of [-1, 1]) if (p.get(Math.round(x + sg * off), Math.round(y)) && hash(s, k, sg + 5) < 0.7) put(x + sg * off, y + (sg > 0 ? 1 : 0), '#6a6e90');
    }
  }
  return p.done();
}
/* ================= Near things: the castle lamp, the café terrace, the old column's drums, a dorożka ================= */
function renderLamp() {
  const p = painter(W, H), put = p.put, { x: lx, y: ly, base } = LAMP;
  for (let y = base - 16; y <= base; y++) {                         // fluted base
    const hw = y > base - 4 ? 5 : y > base - 10 ? 4 : 3;
    for (let i = lx - hw; i <= lx + hw; i++) put(i, y, i === lx - hw ? '#4a4a5c' : (i - lx + 8) % 2 ? '#1c1c26' : '#2a2a38');
  }
  for (const cy of [base - 16, base - 10, base - 4]) for (let i = lx - 5; i <= lx + 5; i++) if (Math.abs(i - lx) <= (cy === base - 16 ? 4 : 5)) put(i, cy, '#565668');
  for (let y = ly + 12; y < base - 16; y++) { put(lx - 1, y, '#3e3e4e'); put(lx, y, '#22222c'); put(lx + 1, y, '#121218'); }
  for (let i = lx - 2; i <= lx + 2; i++) { put(i, ly + 40, '#4a4a5c'); put(i, ly + 12, '#4a4a5c'); }
  // the crosspiece and its two scrolled arms, each with a hanging lantern; a third lantern on top
  for (let i = lx - 12; i <= lx + 12; i++) put(i, ly + 8, '#2a2a36');
  for (const s of [-1, 1]) { put(lx + s * 12, ly + 9, '#2a2a36'); put(lx + s * 12, ly + 10, '#2a2a36'); put(lx + s * 3, ly + 9, '#2a2a36'); put(lx + s * 4, ly + 10, '#2a2a36'); put(lx + s * 5, ly + 10, '#2a2a36'); }
  const lantern = (cx, top) => {
    for (let i = cx - 3; i <= cx + 3; i++) put(i, top, '#2a2a36');
    put(cx, top - 1, '#3a3a48'); put(cx, top - 2, '#4a4a5a');
    for (let y = top + 1; y <= top + 7; y++) for (let i = cx - 3 + (y > top + 5 ? 1 : 0); i <= cx + 3 - (y > top + 5 ? 1 : 0); i++) {
      const fr = Math.abs(i - cx) === 3 - (y > top + 5 ? 1 : 0) || i === cx;
      put(i, y, fr ? '#1a1a22' : y < top + 3 ? '#fff8e0' : '#ffe0a0');
    }
    for (let i = cx - 1; i <= cx + 1; i++) put(i, top + 8, '#22222c');
  };
  lantern(lx, ly - 7); lantern(lx - 12, ly + 11); lantern(lx + 12, ly + 11);
  for (let i = lx - 3; i <= lx + 3; i++) put(i, ly - 8, SNOW[1]);
  return p.done();
}
const HEATER = { x: 5, base: 210, top: 168 };
const PLANTER = { x0: 0, x1: 36, top: 249, base: 263 }, BOARD = { x: 72, base: 254 }, BENCH = { x0: 126, x1: 162, y: 243 };
const TABLE = { x: 36, top: 196, base: 207 };
function renderTerrace() {
  const p = painter(W, H), put = p.put;
  // the patio heater: a steel pole, the burner under its reflector hood glowing, a weighted foot
  const { x: hx, base, top } = HEATER;
  for (let y = top + 8; y < base - 4; y++) { put(hx, y, '#6a6a7a'); put(hx + 1, y, '#3a3a48'); }
  for (let y = base - 4; y <= base; y++) for (let i = hx - 3; i <= hx + 4; i++) put(i, y, y === base - 4 ? '#7a7a8a' : '#2a2a36');
  for (let i = hx - 6; i <= hx + 7; i++) { put(i, top, i < hx ? '#9a9aaa' : '#6a6a7a'); put(i, top + 1, '#2a2a34'); }
  for (let i = hx - 4; i <= hx + 5; i++) put(i, top - 1, SNOW[1]);
  for (let y = top + 2; y <= top + 7; y++) for (let i = hx - 1; i <= hx + 2; i++) put(i, y, (y + i) & 1 ? '#ff8a3a' : '#ffc060');
  // the round café table and two bentwood chairs
  for (let i = TABLE.x - 5; i <= TABLE.x + 5; i++) { put(i, TABLE.top, '#e8dcc0'); put(i, TABLE.top + 1, '#6a5a4a'); }
  for (let y = TABLE.top + 2; y < TABLE.base; y++) put(TABLE.x, y, '#2a2a34');
  for (let i = TABLE.x - 3; i <= TABLE.x + 3; i++) put(i, TABLE.base, '#2a2a34');
  // a planter of small firs by the door, hung with warm bulbs
  for (let y = 196; y < 208; y++) for (let i = 50; i < 56; i++) put(i, y, y === 196 ? '#8a6a4a' : '#4a3226');
  for (let k = 0; k < 3; k++) for (let j = 0; j < 9; j++) for (let i = -((j >> 1) + 1); i <= (j >> 1) + 1; i++) {
    const fx = 51 + k * 2 + (k === 1 ? 0 : 0), fy = 187 + j - k * 2;
    if (fy < 196) put(fx + i, fy, j === 0 ? SNOW[1] : (i + j) % 3 ? '#1e4a34' : '#2e6a4a');
  }
  return p.done();
}
function renderNear() {                                         // the café's planter, its board, a bench on the square
  const p = painter(W, H), put = p.put;
  // a long planter of small firs strung with warm bulbs (the bulbs twinkle per frame)
  const { x0, x1, top, base } = PLANTER;
  for (let y = top; y <= base; y++) for (let i = x0; i <= x1; i++)
    put(i, y, y === top ? SNOW[0] : y === top + 1 ? '#b08a5c' : (i - x0) % 9 === 0 ? '#3a2618' : y > base - 2 ? '#2a1a14' : '#5a3a26');
  for (const [fx, fh] of [[6, 22], [18, 26], [30, 20]]) for (let j = 0; j < fh; j++) {
    const hw = Math.round((j / fh) * 6) + (j % 4 === 3 ? 0 : 1), y = top - fh + j;
    for (let i = -hw; i <= hw; i++) {
      const snowy = j % 4 === 0 && Math.abs(i) < hw;
      put(fx + i, y, snowy ? SNOW[1] : (i + j) % 3 === 0 ? '#2e6a4a' : i < 0 ? '#245a3e' : '#1a4430');
    }
  }
  // the chalkboard: an A-frame on the setts, KAWA and a cup in chalk
  const bx = BOARD.x, bb = BOARD.base;
  for (let y = bb - 26; y <= bb; y++) {
    const hw = 10 + Math.round((y - (bb - 26)) / 13);
    for (let i = bx - hw; i <= bx + hw; i++) {
      const fr = Math.abs(i - bx) >= hw - 1 || y === bb - 26 || y === bb - 3;
      if (y > bb - 3 && Math.abs(i - bx) < hw - 1) continue;
      put(i, y, fr ? '#8a6a46' : '#1e2a26');
    }
  }
  for (let i = bx - 9; i <= bx + 9; i++) put(i, bb - 27, SNOW[1]);
  const cup = ['.s.s...', '..s.s..', 'ccccc..', 'c...cc.', 'c...c.c', 'c...cc.', '.ccc...'];   // a mug in chalk, steam over it
  cup.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') put(bx - 3 + i, bb - 14 + j, '#d8d4c8'); });
  // a park bench of wooden slats on iron legs, snow lying on its seat and its back rail
  const { x0: e0, x1: e1, y: ey } = BENCH;
  for (let i = e0; i <= e1; i++) {
    put(i, ey - 9, SNOW[1]); put(i, ey - 8, '#8a6440'); put(i, ey - 7, '#5a3e28');
    put(i, ey - 5, '#7a5636'); put(i, ey - 4, '#4a3222');
    put(i, ey - 1, SNOW[0]); put(i, ey, '#9a7248'); put(i, ey + 1, '#5a3e28'); put(i, ey + 2, '#16161e');
  }
  for (const lx of [e0 + 2, e1 - 2]) {
    for (let y = ey - 9; y <= ey + 9; y++) { put(lx, y, y > ey + 2 ? '#22222c' : '#2e2e3a'); if (y > ey + 2) put(lx + 1, y, '#16161e'); }
    put(lx - 1, ey + 9, '#22222c'); put(lx + 2, ey + 9, '#16161e');
  }
  for (let i = e0 - 1; i <= e1 + 1; i++) put(i, ey + 10, '#121220');
  const c = p.done();
  sign(p.x, 'KAWA', bx - 9, bb - 22, '#e8e4d8');
  return c;
}
function renderDrums() {                                        // the old column's broken drums by the castle's corner
  const p = painter(W, H), put = p.put;
  for (const [x0, y0, len, a, b] of [[372, 191, 11, '#b07a6a', '#7a4a42'], [385, 190, 9, '#a8a29a', '#6e6a66']]) {
    for (let y = y0; y < y0 + 4; y++) for (let i = x0; i < x0 + len; i++) put(i, y, y === y0 ? SNOW[1] : i === x0 || i === x0 + len - 1 ? b : y === y0 + 1 ? a : b);
  }
  return p.done();
}
/* ================= People ================= */
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const SKINS = ['#f2c8a0', '#e0aa80', '#c08a60', '#8a5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#c8a058', '#8e3e1c', '#c8c4c0'];
const PNIGHT = '#141a3a', GLOW = '#ffd8a0';
// a look for a person h px tall, dressed for a winter night (coats, hats, scarves), dimmed for the dark
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.22, m = c => mixHex(c, PNIGHT, dk);
  const hh = h >= 20 ? 5 : 4, th = Math.round(h * (o.coat ? 0.5 : 0.38));
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 21 ? 7 : h >= 18 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOW, 0.3), top: m(top), topD: mixHex(top, PNIGHT, dk + 0.28),
    topL: mixHex(top, GLOW, 0.32), bot: m(bot), shoe: m('#221a20'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, scarf: o.scarf ? m(o.scarf) : null, beanie: o.beanie ? m(o.beanie) : null };
}
/**
 * One person, feet on row fy, centred on column x; returns the row of the top of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame; o.sit: seated at fy
 * o.aL / o.aR: 0 down, 2 phone up, 3 out (holding, offering), 4 mug raised, 5 hand to the face
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(dir > 0 ? x + 1 : x - 2, ly, 2, legs, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly, 2, legs - 1, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, lw, legs, legC); R(tx + bw - lw, ly, lw, legs, legC);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit && view === 1) {
    R(tx, fy + 1, 2, 4, legC); R(tx + bw - 2, fy + 1, 2, 4, legC); R(tx, fy + 5, 2, 1, P.shoe); R(tx + bw - 2, fy + 5, 2, 1, P.shoe);
  } else if (o.sit && side) {
    const tl = P.h >= 22 ? 7 : 5, sh = o.shin ?? 4;
    R(dir > 0 ? x - 1 : x - tl + 2, fy - 1, tl, 2, P.bot); R(dir > 0 ? x + tl - 3 : x - tl + 2, fy + 1, 2, sh, P.bot); R(dir > 0 ? x + tl - 3 : x - tl + 1, fy + 1 + sh, 3, 1, P.shoe);
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);                                                  // the lamps' light on the shoulders
  if (P.scarf) R(tx, ty, bw, 2, P.scarf);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin); const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 2, 2, 1, '#ffe2a8'); R(p0, hy - 1, 2, 1, '#8ad8e8'); } }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { R(ax, ty, 1, 3, P.topD); R(ax, ty - 1, 1, 1, P.skin); R(ax + (sg < 0 ? -1 : 0), ty - 3, 2, 2, '#f0e8d8'); }
    else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
  };
  if (side) {
    const st = o.aR || 0;
    if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { R(x + dir, ty, 1, 3, P.topD); R(x + dir * 2, ty - 2, 2, 2, '#f0e8d8'); }
    else { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); R(hx - 1, hy, hw + 2, 1, P.hat); }
  if (P.beanie) { R(hx, hy - 1, hw, 2, P.beanie); R(hx + (hw >> 1), hy - 2, 1, 1, '#e8e2d4'); }
  return hy;
}
const TOPS = ['#8a2a34', '#2a4a7a', '#c8a050', '#3a6a5a', '#5a3a6a', '#b0503a', '#4a4a58', '#d8c8a8', '#2a5a8a', '#7a5a3a'];
const BOTS = ['#22202c', '#33466e', '#3a3a48', '#4a3a30'];
/* ================= The scaffold wrap on the second tenement: the billboard ================= */
// Warsaw wraps its scaffolds in printed mesh, and the wrap carries an ad: here a made-up café's hot chocolate, ZORZA
// (dawn), a steaming cup on a chocolate ground, lit by two site floodlights on the top ledger. Without the billboard
// the tenement shows its own front (the scaffold only stands for the sign).
const SCAF_POLES = [54, 72, 90, 108], SCAF_LEDGERS = [112, 138, 162, 185], BB_LAMPS = [BB.x + 9, BB.x + BB.w - 10];
function renderScaffold() {
  const p = painter(W, H), put = p.put, T = TEN[1];
  for (let y = SCAF_LEDGERS[0]; y < SCAF_LEDGERS[3]; y++) for (let x = T.x0; x < T.x1; x++)   // the mesh, dim green over the front
    if ((x + y) % 2 === 0 && bayer(x, y) < 0.6) put(x, y, (x + y * 3) % 7 === 0 ? '#2e3a36' : '#1e2a28', 150);
  for (const x of SCAF_POLES) for (let y = 104; y < T.base; y++) { put(x, y, '#8a8e9a'); put(x + 1, y, '#3a3e4a'); }
  for (const y of SCAF_LEDGERS) for (let x = SCAF_POLES[0] - 2; x <= SCAF_POLES[3] + 3; x++) { put(x, y, '#9a8460'); put(x, y + 1, '#4a3a2a'); }
  for (let k = 0; k < 3; k++) bres(put, SCAF_POLES[k] + 2, SCAF_LEDGERS[2] + 2, SCAF_POLES[k + 1] - 1, SCAF_LEDGERS[3] - 1, '#6a6e7a');
  for (let x = SCAF_POLES[0] - 2; x <= SCAF_POLES[3] + 3; x++) if ((x & 3) === 0) put(x, SCAF_LEDGERS[0] - 3, '#8a8e9a');   // the guard rail
  for (let x = SCAF_POLES[0] - 2; x <= SCAF_POLES[3] + 3; x++) put(x, SCAF_LEDGERS[0] - 4, '#6a6e7a');
  for (const lx of BB_LAMPS) {                                                                 // floodlights on the top ledger
    for (let y = SCAF_LEDGERS[0] - 2; y < SCAF_LEDGERS[0]; y++) put(lx, y, '#3a3a48');
    for (let i = lx - 2; i <= lx + 2; i++) put(i, SCAF_LEDGERS[0] - 3, '#2a2a36');
  }
  return p.done();
}
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h), put = p.put;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const lamp = Math.min(...BB_LAMPS.map(lx => Math.abs(BB.x + x - lx))) / 10;
    put(x, y, ramp(['#7a4228', '#62341f', '#4c2818', '#3a1e14'], clamp(y / h * 0.8 + lamp * 0.3, 0, 1), x, y));
  }
  for (let x = 1; x < w - 1; x++) { put(x, 0, '#c89a5a'); put(x, h - 1, '#8a6a3a'); }
  // the cup on its saucer, a burgundy band, steam curling up
  const CUP = ['..s.s....', '.s.s.s...', '..s.s....', '.........', 'cccccccc.', 'cbbbbbbcc', 'ccccccc.c', 'cccccccc.', '.cccccc..', 'uuuuuuuuu'];
  CUP.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') put(2 + i, 6 + j, { s: '#e8dcd0', c: '#f6ecd8', b: '#a8203e', u: '#d8c8b0' }[row[i]]); });
  const c = p.done();
  const [g, gx] = makeCanvas(w, h);                                  // ZORZA at twice the size, in gold
  sign(gx, 'ZORZA', 0, 0, '#ffd27a');
  p.x.imageSmoothingEnabled = false;
  p.x.drawImage(g, 0, 0, 19, 5, 13, 2, 38, 10);
  sign(p.x, 'CZEKOLADA', 13, 13, '#f2e2c8');
  return gradeAd(c, { level: 0.88, tint: '#141a3a', mix: 0.06, wear: 0.12, seed: 85 });
}
function drawBillboard(t, pitch) {
  ctx.drawImage(SCAF_C, 0, 0);
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 8, 0.8);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const lx of BB_LAMPS) { R(lx - 1, SCAF_LEDGERS[0] - 2, 3, 1, '#f2e8d0'); halo(HL.bbLamp, lx, SCAF_LEDGERS[0] - 1, 0.9); }
}


/* ================= The loop's timeline (seconds) =================
   t          what
   0-20       walkers on three lanes, the busker plays, the café couple sip, smoke from every chimney
   16-40      a melex (the Old Town's electric sightseeing cart) hums out of Świętojańska and off to the right
   24-36      the waiter brings the café couple two fresh mugs (again 84, 144, 204)
   32         someone drops a coin in the busker's case (again 118, 232)
   52-72      the Clock Tower: its clock stage brightens (52), a chime (56), four strokes (60, 63, 66, 69); the pigeons
              lift off the square at the first stroke, circle the column and come down again by 92
   62-118     a parent and child walk out of the shop to the balloon seller, buy a balloon and walk back with it
   66-104     a couple walks across to the dorożka and climbs in (92-102); the coachman lifts his whip, its bell (104)
   104-124    the dorożka trots off right towards Krakowskie Przedmieście
   108-122    two kids by the castle's corner throw snowballs (again 220-234)
   126-170    the snow thickens: a flurry builds (126-136), blows slantwise (136-160) and eases (160-170)
   146-170    the melex comes back and turns into Świętojańska
   172-212    the Clock Tower again: glow (172), chime (176), strokes (180, 183, 186, 189), the pigeons' second flight
   190-212    the dorożka comes back out of the Old Town and draws up at its stand by the castle (bell at 190)
   194-236    a plane crosses high over the castle, its lights blinking
   214-238    the couple steps down and walks off left past the café
   all loop   windows switch on and off in the tenements and the castle, the bulbs twinkle, the horse breathes steam  */
const STRIKES = [{ glow: 52, chime: 56, bells: [60, 63, 66, 69] }, { glow: 172, chime: 176, bells: [180, 183, 186, 189] }];
const BELLS = STRIKES.flatMap(s => s.bells);
const FLIGHTS = [{ up: 60, down: 92 }, { up: 180, down: 212 }];
const DORO_T = { walk: 66, board: 92, inside: 102, leave: 104, gone: 124, back: 190, stop: 212, alight: 214, off: 238 };
const MELEX_T = [{ t0: 16, t1: 40, dir: 1 }, { t0: 146, t1: 170, dir: -1 }];
const WAITER_T = [24, 84, 144, 204];
const KID_T = { t0: 62, buy: 86, back: 92, t1: 118 };
const SNOWBALL_T = [108, 220];
const COIN_T = [32, 118, 232];
const PLANE_T = { t0: 194, t1: 236 };
const FLURRY = { a: 126, b: 170, f: 10 };
const clockGlow = t => STRIKES.reduce((m, s) => Math.max(m, env(t, s.glow, s.bells[3] + 4, 4)), 0);
const bellRing = t => BELLS.reduce((m, b) => Math.max(m, t >= b && t < b + 2.5 ? 1 - (t - b) / 2.5 : 0), 0);

/* ----- chimney smoke: every chimney breathes a slow plume drifting east ----- */
const CHIMNEYS = [
  { x: 285, y: 0, w: 2 }, { x: 357, y: 0, w: 3 }, { x: 439, y: 0, w: 4 }, { x: 470, y: 0, w: 4 },
  { x: 37, y: 41 }, { x: 89, y: 79 }, { x: 133, y: 105 }, { x: 167, y: 126 }, { x: 214, y: 146 }, { x: 241, y: 147 },
].map((c, i) => ({ ...c, ph: hash(i, 5, 61), n: 18 + (i % 3) * 2 }));
for (const c of CHIMNEYS) if (!c.y) {                        // the castle's stacks stand on its roof ridges
  const r = c.x > CAS.xs ? ridgeE(c.x) : ridgeW(c.x), h = { 2: 7, 3: 9, 4: c.x > 460 ? 11 : 12 }[c.w];
  c.y = r - h + 3;
}
function drawSmoke(t, wind) {
  for (const c of CHIMNEYS) for (let j = 0; j < 14; j++) {
    const { u } = cycle(t, c.n, c.ph + j / 14), r = Math.min(5, 1 + Math.floor(u * 6));
    const x = c.x + u * (40 + wind * 30) + Math.sin(TAU * (u * 1.5 + c.ph)) * 3, y = c.y - 2 - u * (48 - wind * 18);
    halo(HL.smoke[r], x, y, 0.62 * (1 - u) * smooth(0, 0.08, u));
  }
}

/* ----- a few of the castle's windows light up and go dark over the loop (drawn in renderFrame with the tenements') ----- */
const CWINS = [];
// two flats with the television on: its light changing behind the glass
const TVS = [{ x: 27, y: 134, w: 6, h: 12 }, { x: 125, y: 146, w: 4, h: 7 }];
function drawTVs(t) {
  const s = step(t, 960);
  TVS.forEach((v, i) => {
    ctx.globalAlpha = 0.45 + 0.4 * hash(i, s, 2);
    R(v.x, v.y, v.w, v.h, ['#5a7ad8', '#8aa8f0', '#3a58b8', '#c8d8ff'][Math.floor(hash(i, s, 3) * 4)]);
    ctx.globalAlpha = 1;
  });
}

/* ----- pigeons: a small flock pecking on the near square; at the first stroke they fly up round the column ----- */
const PIGEONS = [[258, 242], [266, 245], [279, 241], [273, 249], [288, 246], [250, 248]].map(([x, y], i) => ({ x, y, d: i & 1 ? -1 : 1, i }));
function pigeonFlight(p, t) {
  const f = FLIGHTS.find(F => t >= F.up && t < F.down);
  if (!f) return null;
  const u = (t - f.up) / (f.down - f.up), a = TAU * (u * 2.2 + p.i / 6);
  const ring = [COL.x + Math.cos(a) * (26 + p.i * 2), 96 + Math.sin(a) * 8 + p.i * 3];
  if (u < 0.15) { const k = smooth(0, 0.15, u); return [lerp(p.x, ring[0], k), lerp(p.y, ring[1], k)]; }
  if (u > 0.82) { const k = smooth(0.82, 1, u); return [lerp(ring[0], p.x, k), lerp(ring[1], p.y, k)]; }
  return ring;
}
function drawPigeon(p, t) {
  const fl = pigeonFlight(p, t);
  if (fl) {
    const [x, y] = fl.map(Math.round), up = (step(t, 2400) + p.i) & 1;
    R(x - 1, y, 3, 1, '#7a7a90'); R(x - 2, y - (up ? 1 : -1), 2, 1, '#9a9aae'); R(x + 1, y - (up ? 1 : -1), 2, 1, '#9a9aae');
    return;
  }
  const s = step(t, 480), peck = hash(p.i, s, 3) < 0.4, hop = hash(p.i, s >> 2, 4) < 0.3 ? 1 : 0;
  const x = p.x + hop * p.d * ((s >> 2) & 1), y = p.y, d = p.d;
  R(x - 2, y - 2, 5, 2, '#6a6a7e'); R(x - 1, y - 3, 3, 1, '#8a8a9e'); R(x - d * 3, y - 2, 1, 1, '#4a4a5a');
  if (peck) { R(x + d * 3, y - 1, 1, 1, '#5a5a6e'); R(x + d * 4, y, 1, 1, '#c8a050'); }
  else { R(x + d * 2, y - 4, 1, 2, '#5a5a6e'); R(x + d * 3, y - 3, 1, 1, '#c8a050'); }
  R(x, y, 1, 1, '#c86a5a');
}

/* ----- a plane high over the castle, blinking ----- */
function drawPlane(t) {
  if (t < PLANE_T.t0 || t >= PLANE_T.t1) return;
  const u = (t - PLANE_T.t0) / (PLANE_T.t1 - PLANE_T.t0), x = Math.round(lerp(500, -20, u)), y = Math.round(lerp(18, 26, u));
  R(x, y, 3, 1, '#3a3e58');
  const s = step(t, 200);
  if (s % 2 === 0) { R(x - 1, y, 1, 1, '#ff5a4a'); halo(HL.blink, x - 1, y, 0.6); }
  if (s % 4 === 1) { R(x + 3, y, 1, 1, '#ffffff'); halo(HL.blink, x + 3, y, 0.5); }
}

/* ----- the dorożka: a grey horse in a red winter blanket, the black landau with its hood folded, a coachman in a top
   hat on the box. It waits at its stand by the castle, takes a couple off to Krakowskie Przedmieście and comes back
   out of the Old Town. Wheels and legs are drawn per frame (they turn and trot). ----- */
const DORO = { stand: 284, y: 199 };
function renderDorozka() {
  const p = painter(54, 23), put = p.put, G = 22;              // G: the ground row
  // the carriage body: a lacquered black tub, lit along its top, a brass line; the folded hood at the back
  for (let x = 2; x <= 27; x++) {
    const top = x < 10 ? 12 : x < 20 ? 13 : 12, bot = G - 6 + (x > 20 ? -1 : 0);
    for (let y = top; y <= bot; y++) put(x, y, y === top ? '#5a5a6c' : y === top + 2 ? '#b08a3a' : y > bot - 1 ? '#0e0c12' : (x + y) % 9 === 0 ? '#2a2836' : '#18161e');
  }
  for (let k = 0; k < 7; k++) for (let x = 1 + (k >> 1); x <= 10 - (k >> 2); x++) put(x, 11 - k, k === 6 ? '#3a3846' : x === 1 + (k >> 1) ? '#2a2834' : (x & 1) ? '#1e1c26' : '#24222c');   // the hood, ribbed
  for (let x = 11; x <= 18; x++) put(x, 12, '#8a2a34');        // the red seat's back
  // the box: the coachman in his greatcoat and top hat, the whip held up
  for (let y = 9; y <= 13; y++) for (let x = 20; x <= 25; x++) put(x, y, y === 9 ? '#5a5a6c' : '#16141c');
  for (let y = 4; y <= 9; y++) for (let x = 21; x <= 24; x++) put(x, y, x === 21 ? '#4a4a5c' : '#2a2a38');
  for (let y = 2; y <= 3; y++) for (let x = 21; x <= 24; x++) put(x, y, x === 21 ? '#d8b090' : '#c09a7a');
  for (let x = 21; x <= 24; x++) put(x, 0, '#121218');
  for (let x = 20; x <= 25; x++) put(x, 1, '#121218');
  bres(put, 25, 6, 30, 0, '#6a5a4a');
  // the lamps
  put(19, 11, '#2a2a34'); put(19, 10, '#ffe0a0'); put(2, 11, '#2a2a34'); put(2, 10, '#ffd890');
  // the shafts and the harness
  bres(put, 27, 15, 36, 12, '#5a3a24');
  // the horse: grey, a red blanket, dark mane, a red plume between the ears, the tail hanging
  for (let y = 9; y <= 15; y++) for (let x = 34; x <= 46; x++) {
    const d = Math.hypot((x - 40) / 6.5, (y - 12) / 3.6);
    if (d > 1) continue;
    put(x, y, y >= 10 && y <= 12 && x >= 36 && x <= 43 ? (y === 10 ? '#d84a4a' : '#a82a34') : y < 11 ? '#e8e6e2' : y > 13 ? '#9a96a0' : '#c8c4c4');
  }
  for (let k = 0; k <= 5; k++) for (let x = 44 + Math.floor(k * 0.5); x <= 46 + Math.floor(k * 0.6); x++) put(x, 10 - k, x === 44 + Math.floor(k * 0.5) ? '#4a4652' : '#d8d4d2');   // the neck
  for (let y = 3; y <= 7; y++) for (let x = 47; x <= 51; x++) if (!(y === 3 && x > 49) && !(y > 5 && x < 49)) put(x, y, x === 51 || y === 7 ? '#a8a4a8' : '#e2dfdc');   // the head
  put(51, 6, '#5a5056'); put(48, 2, '#c8c4c4'); put(48, 4, '#1a1418');
  put(47, 0, '#e84a4a'); put(47, 1, '#c83a3a'); put(48, 0, '#c83a3a');
  bres(put, 37, 10, 47, 5, '#5a3a24');
  for (let y = 10; y <= 16; y++) put(33 - (y > 13 ? 1 : 0), y, '#6a6670');
  return p.done();
}
const doroAt = t => {
  const T = DORO_T;
  if (t >= T.gone && t < T.back) return null;
  if (t >= T.leave && t < T.gone) { const u = (t - T.leave) / (T.gone - T.leave); return { x: Math.round(DORO.stand + (500 - DORO.stand) * u * u), moving: u > 0.02, a: 1 }; }
  if (t >= T.back && t < T.stop) { const u = (t - T.back) / (T.stop - T.back), e = 1 - (1 - u) * (1 - u); return { x: Math.round(lerp(214, DORO.stand, e)), moving: u < 0.97, a: smooth(0, 0.12, u) }; }
  return { x: DORO.stand, moving: false, a: 1 };
};
function drawDorozka(t, crowd) {
  const d = doroAt(t);
  if (!d || crowd <= 0) return;
  const x0 = d.x, y0 = DORO.y - 22, st = step(t, 1440), trot = d.moving ? st & 1 : 0;
  if (d.a < 1) ctx.globalAlpha = d.a;
  R(x0 + 1, DORO.y, 50, 1, '#0c0c16');                                        // its shadow on the snow
  ctx.drawImage(DORO_C, x0, y0);
  // legs: trotting in diagonal pairs, or standing, one hind hoof resting
  const legs = [[36, 0], [38, 1], [43, 1], [45, 0]];
  for (const [lx, pair] of legs) {
    const lift = d.moving && pair === trot ? 2 : 0, rest = !d.moving && lx === 36 && (step(t, 60) & 1);
    R(x0 + lx + (lift ? 1 : 0), y0 + 15, 1, 6 - lift - (rest ? 1 : 0), lx < 40 ? '#8a8690' : '#b8b4b4');
    R(x0 + lx + (lift ? 1 : 0), y0 + 21 - lift - (rest ? 1 : 0), 1, 1, '#2a2226');
  }
  // the tail swishes now and then
  if (!d.moving && (step(t, 160) % 5 === 0)) R(x0 + 31, y0 + 14, 1, 3, '#6a6670');
  // wheels: a big rear wheel and a small front one, spokes turning with the road
  const wheel = (cx, cy, r) => {
    for (let a = 0; a < 20; a++) { const an = a / 20 * TAU; R(Math.round(x0 + cx + Math.cos(an) * r), Math.round(y0 + cy + Math.sin(an) * r), 1, 1, '#121016'); }
    const rot = d.moving ? (x0 / r) % (TAU / 4) : 0.4;
    for (let k = 0; k < 4; k++) { const an = rot + k * TAU / 8; for (let s = -r + 1; s < r; s++) R(Math.round(x0 + cx + Math.cos(an) * s), Math.round(y0 + cy + Math.sin(an) * s), 1, 1, '#3a3442'); }
    R(x0 + cx, y0 + cy, 1, 1, '#c8a04a');
  };
  wheel(8, 16, 5); wheel(23, 18, 3);
  // the couple riding in it (after they've climbed in until they step down)
  const T = DORO_T;
  if (t >= T.inside && t < T.alight) {
    R(x0 + 12, y0 + 8, 3, 4, '#3a5a8a'); R(x0 + 12, y0 + 5, 3, 3, '#e0b090'); R(x0 + 12, y0 + 5, 3, 1, '#4a2a1a');
    R(x0 + 15, y0 + 8, 3, 4, '#8a2a34'); R(x0 + 15, y0 + 5, 3, 3, '#f0c8a0'); R(x0 + 15, y0 + 5, 3, 1, '#c8a058'); R(x0 + 16, y0 + 8, 2, 4, '#c8a058');
  }
  ctx.globalAlpha = 1;
  halo(HL.doro, x0 + 19, y0 + 10, 0.9 * d.a); halo(HL.doro, x0 + 2, y0 + 10, 0.8 * d.a);
  // the horse's breath in the cold, every few seconds while it stands
  if (!d.moving) {
    const { u } = cycle(t, 60, 0.3);
    if (u < 0.5) halo(HL.breath, x0 + 53 + u * 6, y0 + 6 - u * 4, 0.7 * (1 - u * 2));
  }
}

/* ----- the melex: an electric sightseeing cart, white under its canopy, a guide driving, two rows of visitors ----- */
const MELEX_ROWS = [
  '..cccccccccccccccccccccccccccccc..',
  '..cCCCCCCCCCCCCCCCCCCCCCCCCCCCCc..',
  '..p.........p.........p.......p...',
  '..p..hh.hh..p..hh.hh..p.....w.p...',
  '..p..aa.bb..p..dd.ee..p..gg.w.p...',
  '..p..aa.bb..p..dd.ee..p..ff.w.p...',
  '.ssssssssssssssssssssssssssssssss.',
  'WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWL',
  'WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW',
  'KkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkK',
];
const MELEX_KEY = { c: '#d8d4cc', C: '#b8b4ac', p: '#5a5a66', h: '#2a2420', a: '#8a2a34', b: '#3a5a8a', d: '#4a6a4a', e: '#c8a050',
  g: '#2a3a5a', f: '#e0b090', w: '#7a8aa8', s: '#2a2a34', W: '#e8e6e0', L: '#fff4c8', K: '#121218', k: '#2a2a34' };
const MELEX_Y = 195;                                  // its lane, along the castle front behind the dorożka's stand
const melexAt = t => {
  const m = MELEX_T.find(M => t >= M.t0 && t < M.t1);
  if (!m) return null;
  const u = (t - m.t0) / (m.t1 - m.t0), a = 222, b = 500, x = Math.round(m.dir > 0 ? lerp(a, b, u) : lerp(b, a, u));
  const near = m.dir > 0 ? u : 1 - u;                           // fades in/out at Świętojańska's mouth
  return { x, dir: m.dir, a: smooth(0, 0.08, near) };
};
function drawMelex(t) {
  const m = melexAt(t);
  if (!m) return;
  const c = m.dir > 0 ? MELEX_C : MELEX_L, y = MELEX_Y - 12;
  ctx.globalAlpha = m.a;
  R(m.x + 1, MELEX_Y, c.width - 2, 1, '#0c0c16');
  ctx.drawImage(c, m.x, y);
  for (const wx of [5, c.width - 7]) { R(m.x + wx, MELEX_Y - 2, 3, 2, '#121016'); R(m.x + wx + 1, MELEX_Y - 2 + ((step(t, 1440) + wx) & 1), 1, 1, '#4a4652'); }
  ctx.globalAlpha = 1;
  halo(HL.doro, m.dir > 0 ? m.x + c.width : m.x, y + 7, 0.8 * m.a);
}

/* ================= The cast: people who stay, each with a verb on the loop =================
   rank: drawn while rank < crowd (Settings, People); rank 0 is there at every level above 0 (the café couple and
   their waiter, the busker, the balloon seller, the actors of the timed moments); pairs share one rank */
const rk = i => frac((i + 0.5) * 0.618);
const L = (h, skin, hair, top, bot, o) => look(h, skin, hair, top, bot, o);
const P_HER = L(19, 0, 3, TOPS[0], BOTS[0], { long: 3, scarf: '#e8d8b0' }), P_HIM = L(20, 1, 1, TOPS[1], BOTS[1], { beanie: '#c84a3a' });
const P_WAITER = L(20, 1, 0, '#e8e4dc', '#1a1a22', { dk: 0.12 }), P_BUSKER = L(20, 2, 2, '#4a3a2a', BOTS[0], { hat: '#2a2226', scarf: '#c8a050' });
const P_SELLER = L(15, 2, 1, TOPS[6], BOTS[3], { dk: 0.28, beanie: '#3a6a5a' }), P_PHOTO = L(21, 1, 2, TOPS[2], BOTS[0], { scarf: '#8a2a34', coat: true });
const P_SIT_A = L(13, 1, 0, TOPS[5], BOTS[1], { dk: 0.3 }), P_SIT_B = L(13, 0, 4, TOPS[8], BOTS[0], { dk: 0.3, long: 2 });
const P_KID = L(12, 0, 3, '#e8c040', BOTS[1], { dk: 0.18, beanie: '#d84a6a' }), P_PARENT = L(19, 1, 1, TOPS[3], BOTS[2], { coat: true });
const P_SB1 = L(12, 1, 4, '#3a8ad8', BOTS[0], { dk: 0.2, beanie: '#e8e2d4' }), P_SB2 = L(12, 2, 0, '#d86a3a', BOTS[1], { dk: 0.2, beanie: '#3a6a3a' });
const P_DORO_HIM = L(20, 0, 1, '#3a5a8a', BOTS[0], { coat: true, scarf: '#e8d8b0' }), P_DORO_HER = L(19, 0, 3, '#8a2a34', BOTS[0], { long: 3, coat: true });
const BUSKER = { x: 128, y: 205 }, SELLER = { x: 226, y: 200 }, PHOTO = { x: 246, y: 205 }, SB = [{ x: 384, y: 204 }, { x: 412, y: 204 }];
const RANK = { photo: rk(0), sitA: rk(3), sitB: rk(5), kids: rk(8) };
// someone seen in the wet stone under them: the same figure flipped about its feet, faint
function mirror(fy, draw, a = 0.18) {
  ctx.save(); ctx.globalAlpha = a; ctx.setTransform(1, 0, 0, -1, 0, 2 * fy + 1); draw(); ctx.restore();
}
function withShadow(x, fy, w) { ctx.globalAlpha = 0.5; R(x - (w >> 1), fy + 1, w, 1, '#0a0a14'); ctx.globalAlpha = 1; }

// the café couple: mugs to their lips in turn, leaning in to talk every 40 s; steam off the mugs
function drawCouple(t) {
  const herSip = frac(t / 12) < 0.2, himSip = frac(t / 12 + 0.5) < 0.2, talk = frac(t / 40 + 0.3) < 0.18;
  person(29, 202, P_HER, { view: 2, dir: 1, sit: true, aR: herSip ? 4 : 0, lean: talk ? 1 : 0 });
  person(44, 202, P_HIM, { view: 2, dir: -1, sit: true, aR: himSip ? 4 : 0, lean: talk ? -1 : 0 });
  if (!herSip) R(32, TABLE.top - 2, 2, 2, '#f0e8d8');
  if (!himSip) R(39, TABLE.top - 2, 2, 2, '#f0e8d8');
  for (const [mx, k] of [[herSip ? 31 : 32, 0], [himSip ? 46 : 39, 1]]) {
    const { u } = cycle(t, 80, k * 0.5);
    const sy = Math.max(184, TABLE.top - 4 - Math.round(u * 6) - (k && himSip ? 8 : herSip && !k ? 8 : 0));   // stays under the café's sign
    ctx.globalAlpha = 0.5 * (1 - u); R(mx + Math.round(Math.sin(u * 6 + k) * 1), sy, 1, 1, '#e8e8f0'); ctx.globalAlpha = 1;
  }
}
// the waiter: out of the café door with a tray of two mugs, to the table, back in
function waiterAt(t) {
  const w = WAITER_T.find(a => t >= a && t < a + 12);
  if (w === undefined) return null;
  const u = t - w;
  if (u < 3) return { x: Math.round(lerp(28, 54, u / 3)), dir: 1, step: 1, tray: true, a: smooth(0, 0.8, u) };
  if (u < 8) return { x: 54, dir: -1, step: 0, tray: u < 6, a: 1 };
  return { x: Math.round(lerp(54, 28, (u - 8) / 4)), dir: -1, step: 1, tray: false, a: 1 - smooth(3.2, 4, u - 8) };
}
function drawWaiter(t) {
  const w = waiterAt(t);
  if (!w) return;
  ctx.globalAlpha = w.a;
  const hy = person(w.x, 205, P_WAITER, { view: 2, dir: w.dir, step: w.step && (step(t, 768) & 1), aR: w.tray || w.dir < 0 && w.x === 54 ? 3 : 0 });
  R(w.x - 1, hy + 9, 3, 6, '#f2eee6');                                          // his long apron
  if (w.tray) { R(w.x + w.dir * 3 - 2, hy + 6, 5, 1, '#c8c8d0'); R(w.x + w.dir * 3 - 2, hy + 4, 2, 2, '#f0e8d8'); R(w.x + w.dir * 3 + 1, hy + 4, 2, 2, '#f0e8d8'); }
  ctx.globalAlpha = 1;
}
// the busker: a violinist under the green house, his case open on the stones, a coin now and then
function drawBusker(t) {
  const { x, y } = BUSKER, bow = Math.round(wave(t, 120) * 2);
  const hy = person(x, y, P_BUSKER, { view: 1, aL: 0, aR: 0 });
  R(x + 2, hy + 5, 3, 2, '#8a4a24'); R(x + 4, hy + 4, 1, 1, '#5a2a14');      // the violin under his chin
  R(x - 4, hy + 7, 1, 1, P_BUSKER.skin); R(x - 3, hy + 6, 2, 1, P_BUSKER.topD);
  ctx.fillStyle = '#d8c8a8';
  for (let k = 0; k < 7; k++) ctx.fillRect(x - 3 + bow + k, hy + 7 - (k >> 1), 1, 1);                 // the bow, drawn back and forth
  R(x + 1, hy + 6, 1, 2, P_BUSKER.topD);
  R(x + 5, y, 9, 2, '#3a1e14'); R(x + 6, y, 7, 1, '#8a2a34'); R(x + 5, y - 3, 9, 1, '#2a1610');   // the case and its lid
  const c = COIN_T.find(a => t >= a && t < a + 2.5);
  R(x + 8, y, 1, 1, '#e8c860'); R(x + 10, y, 1, 1, '#c8a040');
  if (c !== undefined) { const u = (t - c) / 2.5; R(x + 9, y - 1, 1, 1, '#fff2b0'); halo(HL.coin, x + 9, y - 1, 1 - u); }
}
// the balloon seller and his glowing bunch, swaying on its strings
const BALLOONS = [[-3, -16, '#e84a6a'], [0, -19, '#ffd860'], [3, -16, '#5ac8e8'], [-1, -13, '#b86ae8'], [2, -12, '#ff8a3a'], [-4, -11, '#6ae88a']];
function drawSeller(t) {
  const { x, y } = SELLER, sell = t >= KID_T.buy && t < KID_T.back;
  const hy = person(x, y, P_SELLER, { view: 1, aR: 3, aL: sell ? 3 : 0 });
  const hx = x + 4, hyy = hy + 6;
  BALLOONS.forEach(([bx, by, col], i) => {
    const sw = Math.round(wave(t, 16 + i * 4, i * 0.21) * 1.2 + wave(t, 6, 0) * (1 + env(t, FLURRY.a, FLURRY.b, FLURRY.f)));
    const ex = hx + bx + sw, ey = hy - 4 + by;
    ctx.fillStyle = '#9a96a8';
    for (let s = 2; s < hyy - ey; s += 2) ctx.fillRect(Math.round(lerp(ex, hx, s / (hyy - ey))), ey + s, 1, 1);
    R(ex - 1, ey - 1, 3, 3, col); R(ex - 1, ey - 1, 1, 1, '#ffffff'); halo(HL.balloon, ex, ey, 0.55);
  });
}
// a parent and child walk out of the shop to the balloon seller, buy one, and walk back with it bobbing over them
function drawKid(t) {
  if (t < KID_T.t0 || t >= KID_T.t1) return;
  let x, dir, walking, a = 1;
  if (t < KID_T.buy) { const u = (t - KID_T.t0) / (KID_T.buy - KID_T.t0); x = lerp(66, 214, u); dir = 1; walking = true; a = smooth(0, 0.04, u); }
  else if (t < KID_T.back) { x = 214; dir = 1; walking = false; }
  else { const u = (t - KID_T.back) / (KID_T.t1 - KID_T.back); x = lerp(214, 66, u); dir = -1; walking = true; a = 1 - smooth(0.95, 1, u); }
  x = Math.round(x);
  const st = walking && (step(t, 768) & 1), has = t >= KID_T.buy + 3;
  ctx.globalAlpha = a;
  person(x - dir * 7, 203, P_PARENT, { view: 2, dir, step: st });
  const khy = person(x, 203, P_KID, { view: 2, dir, step: !st && walking, aR: has || !walking ? 3 : 0 });
  if (has) { const bx = x + dir * 3 + Math.round(wave(t, 30) * 1), by = khy - 9; ctx.fillStyle = '#9a96a8'; for (let s = 1; s < 8; s += 2) ctx.fillRect(bx, by + 2 + s, 1, 1); R(bx - 1, by - 1, 3, 3, '#e84a6a'); R(bx - 1, by - 1, 1, 1, '#ffffff'); halo(HL.balloon, bx, by, 0.55 * a); }
  ctx.globalAlpha = 1;
}
// the photographer: phone up at the column and the castle, then lowered, looking round
function drawPhoto(t) {
  const up = frac(t / 24 + 0.15) < 0.6, { x, y } = PHOTO;
  if (up) { person(x, y, P_PHOTO, { view: 0, aR: 2, aL: 2 }); halo(HL.phone, x, y - 24, 0.6); }
  else person(x, y, P_PHOTO, { view: 2, dir: frac(t / 48) < 0.5 ? -1 : 1 });
}
// two people sitting on the column's steps: one on a phone, its screen lighting her face
function drawSitters(t, crowd) {
  if (RANK.sitA < crowd) person(184, 194, P_SIT_A, { view: 1, sit: true, lean: frac(t / 30) < 0.2 ? 1 : 0 });
  if (RANK.sitB < crowd) { person(213, 194, P_SIT_B, { view: 1, sit: true, aR: frac(t / 20) < 0.7 ? 5 : 0 }); halo(HL.phone, 214, 185, 0.45); }
}
// two kids by the castle's corner: scooping snow, throwing, a puff where it lands
function drawSnowball(t) {
  const w = SNOWBALL_T.find(a => t >= a && t < a + 14), u = w === undefined ? -1 : t - w;
  const throwA = u >= 1.5 && u < 2.5, throwB = u >= 6.5 && u < 7.5, scoopA = u >= 0 && u < 1.5, scoopB = u >= 5 && u < 6.5;
  person(SB[0].x, SB[0].y, P_SB1, { view: 2, dir: 1, aR: throwA ? 4 : scoopA ? 3 : 0 });
  person(SB[1].x, SB[1].y, P_SB2, { view: 2, dir: -1, aR: throwB ? 4 : scoopB ? 3 : 0 });
  for (const [t0, from, to] of [[2.2, SB[0], SB[1]], [7.2, SB[1], SB[0]]]) {
    if (u < t0 || u >= t0 + 1.6) continue;
    const k = (u - t0) / 1.2;
    if (k < 1) R(Math.round(lerp(from.x + 3 * Math.sign(to.x - from.x), to.x, k)), Math.round(lerp(from.y - 10, to.y - 6, k) - Math.sin(Math.PI * k) * 8), 1, 1, '#f4f6ff');
    else { ctx.globalAlpha = 1 - (k - 1) / 0.33; R(to.x - 1, to.y - 7, 3, 1, '#e8ecff'); R(to.x, to.y - 8, 1, 3, '#e8ecff'); ctx.globalAlpha = 1; }
  }
}
// the couple who take the dorożka: across from the café side to the stand, in, and later down again and away left
function doroCouple(t) {
  const T = DORO_T;
  if (t >= T.walk && t < T.board) { const u = (t - T.walk) / (T.board - T.walk); return { x: Math.round(lerp(-8, 280, u)), y: Math.round(lerp(207, 203, smooth(0.7, 1, u))), dir: 1, step: 1, a: 1 }; }
  if (t >= T.board && t < T.inside) return { x: 280, y: 203, dir: 1, step: 0, a: 1 - smooth(T.inside - 2, T.inside, t) };
  if (t >= T.alight && t < T.off) { const u = (t - T.alight) / (T.off - T.alight); return { x: Math.round(lerp(280, -10, smooth(0.08, 1, u))), y: Math.round(lerp(203, 207, smooth(0.1, 0.3, u))), dir: -1, step: u > 0.08 ? 1 : 0, a: smooth(0, 0.06, u) }; }
  return null;
}
function drawDoroCouple(t) {
  const c = doroCouple(t);
  if (!c) return;
  const st = c.step && (step(t, 768) & 1), point = !c.step && c.a > 0.9;
  ctx.globalAlpha = c.a;
  person(c.x - c.dir * 7, c.y, P_DORO_HIM, { view: 2, dir: c.dir, step: st });
  person(c.x, c.y, P_DORO_HER, { view: 2, dir: c.dir, step: !st && c.step, aR: point ? 3 : 0 });
  ctx.globalAlpha = 1;
}

/* ================= Walkers: three lanes across the square ================= */
// far (feet 197, along the castle front out of Świętojańska), middle (203, out of the shop under the scaffold), near
// (207, right across the frame); each crosses a whole number of times a loop, some in pairs, with a dog, a bag, a
// paper cup or an umbrella against the snow
const LANES = { F: { y: 197, a: 224, b: 500, h: 13, dk: 0.32 }, M: { y: 203, a: 64, b: 500, h: 18, dk: 0.2 }, N: { y: 207, a: -14, b: 494, h: 21, dk: 0.14 } };
const WALKERS = [];
{
  const defs = [
    ['N', 2, 0.05, 1, 6, 'dog'], ['N', 3, 0.55, -1, 13, 'cup'], ['M', 2, 0.3, -1, 11, 'cup'], ['M', 3, 0.8, 1, 10, 'pair'],
    ['F', 3, 0.2, 1, 16, null], ['N', 2, 0.35, 1, 2, 'bag'], ['M', 2, 0.6, 1, 7, null], ['F', 2, 0.7, -1, 4, 'pair'],
    ['N', 3, 0.9, -1, 12, 'cup'], ['F', 3, 0.45, -1, 9, null], ['M', 3, 0.1, -1, 15, 'umbrella'], ['N', 2, 0.75, -1, 14, 'pair'],
    ['M', 2, 0.15, 1, 18, 'bag'], ['N', 2, 0.6, 1, 21, null], ['F', 2, 0.85, 1, 24, 'pair'],
  ];
  const r = rng(77);
  defs.forEach(([lane, k, ph, dir, ri, extra], i) => {
    const Ln = LANES[lane], h = Ln.h + (r() < 0.5 ? 0 : 1);
    const P = look(h, Math.floor(r() * 4), Math.floor(r() * 6), TOPS[(i * 3 + 1) % TOPS.length], BOTS[i % BOTS.length],
      { dk: Ln.dk, coat: r() < 0.6, long: r() < 0.35 ? 2 : 0, beanie: r() < 0.35 ? ['#c84a3a', '#3a6a8a', '#e8e2d4'][i % 3] : null, hat: r() < 0.15 ? '#2a2a34' : null });
    const P2 = extra === 'pair' ? look(h - 1, Math.floor(r() * 4), Math.floor(r() * 6), TOPS[(i * 5 + 4) % TOPS.length], BOTS[(i + 1) % BOTS.length], { dk: Ln.dk, long: 3, coat: true }) : null;
    WALKERS.push({ lane, k, ph, dir, P, P2, extra, rank: rk(ri), umb: ['#2a3a6a', '#8a2a34', '#2a2a34'][i % 3] });
  });
}
function walkerAt(w, t) {
  const Ln = LANES[w.lane], span = Ln.b - Ln.a, u = frac(w.k * t / LOOP + w.ph);
  const x = w.dir > 0 ? Ln.a + span * u : Ln.b - span * u;
  const fromStart = w.dir > 0 ? u : 1 - u;                        // how far from the lane's start (the shop, the apex)
  const a = w.lane === 'N' ? 1 : smooth(0, 0.03, fromStart);
  return { x: Math.round(x), y: Ln.y, a };
}
function drawWalker(w, t, refl) {
  const { x, y, a } = walkerAt(w, t), st = (step(t, 768) + w.k) & 1, dir = w.dir;
  const draw = () => {
    if (w.P2) person(x - dir * 6, y, w.P2, { view: 2, dir, step: !st });
    const hy = person(x, y, w.P, { view: 2, dir, step: st, aR: w.extra === 'cup' || w.extra === 'dog' ? 3 : 0 });
    if (w.extra === 'cup') { R(x + dir * 4, hy + 6, 1, 2, '#e8dcc8'); }
    if (w.extra === 'bag') { R(x - dir * 2 - 1, hy + 10, 3, 4, '#c8a050'); }
    if (w.extra === 'umbrella') {
      const uy = hy - 4, cx = x + dir;
      R(cx - 6, uy + 1, 13, 1, w.umb); R(cx - 4, uy, 9, 1, w.umb); R(cx - 2, uy - 1, 5, 1, w.umb);
      R(cx - 6, uy, 13, 1, '#e8ecff'); R(cx, uy + 2, 1, hy - uy + 6, '#2a2a30');                                   // snow on the canopy
    }
    if (w.extra === 'dog') {
      const dx = x + dir * 9, dst = (step(t, 1536) + 1) & 1;
      R(dx - 3, y - 4, 7, 3, '#5a3e2a'); R(dir > 0 ? dx + 3 : dx - 4, y - 6, 2, 3, '#5a3e2a'); R(dir > 0 ? dx + 4 : dx - 5, y - 6, 1, 1, '#2a1a12');
      R(dir > 0 ? dx - 4 : dx + 4, y - 5, 1, 1, '#5a3e2a');
      for (const lx of [-2, 2]) R(dx + lx + (dst && lx > 0 ? 1 : 0), y - 1, 1, 2, '#3a2a1e');
      ctx.fillStyle = '#8a2a2a'; for (let s = 0; s < 6; s++) ctx.fillRect(Math.round(lerp(x + dir * 4, dx + dir * 2, s / 6)), hy + 8 + (s >> 1), 1, 1);
    }
  };
  if (refl) { if (w.lane !== 'F') mirror(y, draw, 0.16 * a); return; }
  if (a < 1) ctx.globalAlpha = a;
  draw();
  ctx.globalAlpha = 1;
}

/* ================= Everything on the square, in feet order ================= */
function drawPeople(t, crowd) {
  const DL = [], on = crowd > 0;
  // their faint images in the wet stone first, under everyone
  for (const w of WALKERS) if (w.rank < crowd) drawWalker(w, t, true);
  if (on) { mirror(BUSKER.y, () => person(BUSKER.x, BUSKER.y, P_BUSKER, { view: 1 })); }
  if (RANK.photo < crowd) mirror(PHOTO.y, () => person(PHOTO.x, PHOTO.y, P_PHOTO, { view: 0 }));
  for (const w of WALKERS) if (w.rank < crowd) DL.push([walkerAt(w, t).y, () => drawWalker(w, t, false)]);
  if (on) {
    DL.push([207, () => drawCouple(t)]);
    DL.push([205, () => drawWaiter(t)]);
    DL.push([BUSKER.y, () => drawBusker(t)]);
    DL.push([SELLER.y, () => drawSeller(t)]);
    DL.push([203.5, () => drawKid(t)]);
    const c = doroCouple(t);
    if (c) DL.push([c.y + 0.5, () => drawDoroCouple(t)]);
  }
  if (RANK.photo < crowd) DL.push([PHOTO.y, () => drawPhoto(t)]);
  if (RANK.kids < crowd) DL.push([SB[0].y, () => drawSnowball(t)]);
  DL.push([197.5, () => drawSitters(t, crowd)]);
  DL.push([DORO.y, () => drawDorozka(t, crowd)]);
  DL.push([195, () => drawMelex(t)]);
  DL.push([207.6, () => ctx.drawImage(TERRACE_C, 0, 0)]);
  DL.push([LAMP.base, () => ctx.drawImage(LAMP_C, 0, 0)]);
  DL.push([BOARD.base, () => ctx.drawImage(NEAR_C, 0, 0)]);
  for (const p of PIGEONS) DL.push([pigeonFlight(p, t) ? 0 : p.y, () => drawPigeon(p, t)]);
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, draw] of DL) draw();
}

/* ================= Warsaw's own snow at three depths, and the flurry ================= */
// far flakes small and slow, settling on the roofs and the far square; mid ones over the square; near ones, bigger,
// drifting past everything. Each sways on its way down; flakes near a lamp catch its light. In the flurry more flakes
// fall (each decides at the top of its fall, so none pops up mid-air) and the wind leans them east.
const FLAKES = [
  { n: 230, base: 150, nMin: 14, nMax: 22, size: 1, col: '#aab2d8', lit: '#e8c8a0', a: 0.55, land: [70, 192], sway: 3 },
  { n: 170, base: 110, nMin: 24, nMax: 34, size: 1, col: '#ccd4f2', lit: '#ffe0b8', a: 0.75, land: [186, 236], sway: 5 },
  { n: 90, base: 54, nMin: 32, nMax: 46, size: 2, col: '#eef2ff', lit: '#fff0d8', a: 0.85, land: [210, H + 6], sway: 8 },
].map((L, d) => ({ ...L, flakes: Array.from({ length: L.n }, (_, i) => ({ n: L.nMin + Math.floor(hash(i, d, 71) * (L.nMax - L.nMin)), ph: hash(i, d, 72), s: i * 7 + d, k: 6 + Math.floor(hash(i, d, 75) * 10), extra: i >= L.base })) }));
const flurry = t => env(t, FLURRY.a, FLURRY.b, FLURRY.f);
const SNOW_LIGHTS = [{ x: 30, y: 190, r: 40 }, { x: LAMP.x, y: LAMP.y + 6, r: 40 }, { x: HEATER.x, y: HEATER.top + 4, r: 22 }, { x: COL.x, y: 50, r: 22 }];
const SNOW_LIGHTS_BB = [...SNOW_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y, r: 26 }];
const litAt = (x, y, lights) => lights.some(l => (x - l.x) ** 2 + (y - l.y) ** 2 < l.r * l.r);
function drawSnow(depth, t, lights) {
  const L = FLAKES[depth], wind = flurry(t);
  ctx.globalAlpha = L.a;
  for (const f of L.flakes) {
    const { cyc, u } = cycle(t, f.n, f.ph);
    if (f.extra && hash(f.s, cyc, 76) > flurry(t - u * LOOP / f.n) * 1.1) continue;
    const land = lerp(L.land[0], L.land[1], hash(f.s, cyc, 73));
    const x0 = hash(f.s, cyc, 74) * (W + 60) - 50, y = Math.round(lerp(-6, land, u));
    const x = Math.round(x0 + Math.sin(TAU * (f.k * t / LOOP) + f.s) * L.sway * 0.5 + u * L.sway + wind * (y + 6) * 0.35);
    ctx.fillStyle = litAt(x, y, lights) ? L.lit : L.col;
    ctx.fillRect(x, y, L.size, L.size);
  }
  ctx.globalAlpha = 1;
}
const WEATHER = weatherPlans(['snow']);
const WX_LAND = [[100, 192], [188, 232], [208, H + 4]];
const WX = { land: WX_LAND, lights: SNOW_LIGHTS.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })), leaves: { wind: [0, 0, W, 200] }, density: 0.6 };
const WX_BB = { ...WX, lights: SNOW_LIGHTS_BB.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })) };

function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = Math.hypot((x - W / 2) / (W * 0.62), (y - H * 0.45) / (H * 0.7));
    if (d > 0.82 && (d - 0.82) * 3.2 > bayer(x, y)) p.put(x, y, '#05060e', 90);
  }
  return p.done();
}

/* ================= The wet square's mirror ================= */
// Everything static above the square, mirrored about each thing's own foot and smeared down the way wet stone smears
// it (lit windows become streaks), fading with depth. Drawn per frame row by row with a ripple; the people and the
// lights that change are mirrored or streaked on their own.
function renderMirror() {
  const [src, sx] = makeCanvas(W, H);
  for (const c of [SKY_C, FAR_C, CASTLE_C, TOWN_C, COLUMN_C]) sx.drawImage(c, 0, 0);
  const sd = sx.getImageData(0, 0, W, H).data, [c, x] = makeCanvas(W, H), img = x.createImageData(W, H), d = img.data;
  const wet = hex('#1c1e32');
  for (let i = 0; i < W; i++) {
    const b = Math.abs(i - COL.x) <= 21 ? COL.ground + 1 : footY(i);
    for (let y = b; y < H; y++) {
      const k = y - b, sy = b - 1 - k;
      if (sy < 2) break;
      let r = 0, g = 0, bl = 0, n = 0;
      for (let j = -3; j <= 3; j++) { const yy = sy + j; if (yy < 0 || yy >= b) continue; const o = (yy * W + i) * 4; r += sd[o]; g += sd[o + 1]; bl += sd[o + 2]; n++; }
      const o = (y * W + i) * 4, a = clamp(0.42 * (1 - k / 95), 0, 1);
      d[o] = lerp(wet[0], r / n, 0.85); d[o + 1] = lerp(wet[1], g / n, 0.85); d[o + 2] = lerp(wet[2], bl / n, 0.85); d[o + 3] = Math.round(a * 255);
    }
  }
  x.putImageData(img, 0, 0);
  return c;
}
function drawMirror(t) {
  for (let y = 187; y < H; y++) {
    const k = y - 187, off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.55) * (0.5 + k * 0.035) + Math.sin(TAU * 110 * t / LOOP - k * 1.3) * 0.7);
    ctx.drawImage(MIRROR_C, 0, y, W, 1, off, y, W, 1);
  }
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, WISP_C, FAR_C, CASTLE_C, TOWN_C, COLUMN_C, GROUND_C, LAMP_C, TERRACE_C, DRUMS_C, DORO_C,
  SCAF_C, BB_AD, PITCH, VIG_C, SNOWL_C, NEAR_C, MELEX_C, MELEX_L, MIRROR_C, MIST_C;
function prerender() {
  WINS.length = 0; CWINS.length = 0;
  HL = {
    moon: makeHalo(44, '#9ab0ff', 0.2, 1.8), moonCore: makeHalo(16, '#fff2d0', 0.3, 1.5),
    castle: makeHalo(90, '#ff9a50', 0.1, 1.4), up: makeHalo(12, '#ffb870', 0.32, 1.5), clock: makeHalo(26, '#ffd890', 0.3, 1.5),
    lantern: makeHalo(8, '#ffd890', 0.4, 1.4), statue: makeHalo(20, '#d8f0ff', 0.18, 1.5), shaft: makeHalo(30, '#ffe8c8', 0.08, 1.5),
    cafe: makeHalo(44, '#ffb060', 0.28, 1.5), heater: makeHalo(20, '#ff8a3a', 0.4, 1.4), lamp: makeHalo(54, '#ffc070', 0.22, 1.7),
    lampCore: makeHalo(12, '#fff0c8', 0.55, 1.4), win: makeHalo(8, '#ffb45a', 0.18), balloon: makeHalo(4, '#ffffff', 0.25, 1.3),
    bbLamp: makeHalo(6, '#fff0d0', 0.35, 1.6), bbWash: makeHalo(34, '#ffd8a8', 0.08, 1.5), doro: makeHalo(5, '#ffe0a0', 0.5, 1.3),
    haze: makeHalo(140, '#ff9a5a', 0.06, 1.3), bulb: makeHalo(4, '#ffc860', 0.45, 1.2), blink: makeHalo(3, '#ffffff', 0.5, 1.2),
    breath: makeHalo(3, '#e8ecff', 0.5, 1.2), coin: makeHalo(4, '#fff2b0', 0.6, 1.2), phone: makeHalo(5, '#a8e0ff', 0.35, 1.3),
    smoke: [0, 1, 2, 3, 4, 5].map(r => makeHalo(r + 1, '#9298b8', 0.5, 0.7)), bell: makeHalo(36, '#ffd890', 0.22, 1.5),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); WISP_C = WISPS.map(renderWisp);
  FAR_C = renderFar(); CASTLE_C = renderCastle(); TOWN_C = renderTown(); COLUMN_C = renderColumn(); GROUND_C = renderGround();
  LAMP_C = renderLamp(); TERRACE_C = renderTerrace(); DRUMS_C = renderDrums(); SNOWL_C = renderSnowLayer(); NEAR_C = renderNear();
  DORO_C = renderDorozka(); MELEX_C = sprite(MELEX_ROWS, MELEX_KEY); MELEX_L = sprite(MELEX_ROWS, MELEX_KEY, true);
  SCAF_C = renderScaffold(); BB_AD = renderBillboardAd();
  PITCH = makePitch(BB.w, BB.h, { accent: '#e05a7a' });
  VIG_C = renderVignette(); MIRROR_C = renderMirror(); MIST_C = [renderMist(23, 40), renderMist(29, 34)];
}

/* ================= Frame compositor (back to front) ================= */
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
const UPLIGHTS = [...AXES_W.filter(a => a < CT.x0 - 2 || a > CT.x1 + 2), ...AXES_E];
// lights laid on the wet stone down to the bottom of the frame: the lamp, the café, the shops, the column's floodlight,
// the castle's uplights and its Clock Tower ([x, width, colour, alpha, from row])
const STREAKS = [[LAMP.x, 6, '#ffc070', 0.3, 228], [LAMP.x - 12, 4, '#ffc070', 0.22, 232], [LAMP.x + 12, 3, '#ffc070', 0.2, 234],
  [12, 8, '#ffb060', 0.26, 210], [46, 8, '#ffb060', 0.24, 210], [82, 5, '#f2b464', 0.18, 205], [100, 4, '#f2b464', 0.16, 205],
  [127, 4, '#f2b464', 0.16, 200], [162, 3, '#f2b464', 0.14, 196], [COL.x, 4, '#e8e0d0', 0.16, 201], [232, 3, '#f2b464', 0.12, 190],
  [266, 3, '#ffb870', 0.14, 194], [290, 3, '#ffb870', 0.15, 194], [CT.x0 + 12, 6, '#ffb060', 0.22, 196], [345, 3, '#ffb870', 0.16, 195],
  [378, 4, '#ffb870', 0.18, 196], [404, 4, '#ffb870', 0.18, 196], [425, 3, '#ffb870', 0.16, 196]];
// figures moving behind the café's lit arches
function drawCafeInside(t) {
  ctx.save(); ctx.beginPath(); ctx.rect(4, 182, 14, 22); ctx.rect(37, 182, 14, 22); ctx.clip();
  for (let k = 0; k < 3; k++) {
    const x = Math.round(28 + wave(t, 3 + k * 2, k * 0.31) * 24), y = 190 + k;
    R(x - 1, y, 3, 3, '#7a4426'); R(x - 2, y + 3, 5, 10, '#6a3a20');
  }
  ctx.restore();
}
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd = CROWD, clock } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, lights = billboard ? SNOW_LIGHTS_BB : SNOW_LIGHTS;
  const glow = clockGlow(t), ring = bellRing(t), wind = wx.snow ? flurry(t) : 0;
  // sky, stars, moon, wisps, a plane
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e8eeff';
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph));
    ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha *= 0.4; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  for (const L of WISP_C) drawScroll(L.c, L.y, L.k, t);
  drawPlane(t);
  ctx.globalAlpha = 0.5 + 0.2 * wave(t, 3, 0.1); drawScroll(MIST_C[0], 92, 1, t);
  ctx.globalAlpha = 0.4 + 0.2 * wave(t, 4, 0.6); drawScroll(MIST_C[1], 118, 2, t); ctx.globalAlpha = 1;
  // the square, the far houses and St John's, the castle in its floodlight
  ctx.drawImage(GROUND_C, 0, 0);
  drawMirror(t);
  halo(HL.haze, 330, 196, 1);
  ctx.drawImage(FAR_C, 0, 0);
  if (wx.snow) drawSnow(0, t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  halo(HL.castle, 380, 190, 0.9);
  ctx.drawImage(CASTLE_C, 0, 0);
  const ccx = (CT.x0 + CT.x1) >> 1;
  halo(HL.clock, ccx, 102, 0.75 + 0.35 * glow);
  if (glow > 0) { ctx.globalAlpha = 0.5 * glow; R(CT.x0 + 1, 111, CT.x1 - CT.x0 - 1, 6, '#fff2c0'); ctx.globalAlpha = 1; }
  if (ring > 0) halo(HL.bell, ccx, 100, ring * 0.8);
  hands(ctx, ccx, 98, clock, 1300, 2, 3, GILT[0]);
  halo(HL.lantern, ccx, 63, 0.7 + 0.3 * glow); halo(HL.lantern, (SWT.x0 + SWT.x1) >> 1, 83, 0.5);
  for (const ax of UPLIGHTS) {
    const b = ax > CAS.xs ? baseE(ax) : baseW(ax);
    R(ax, b - 1, 2, 1, '#fff2d0'); halo(HL.up, ax + 1, b - 2, 0.7);
  }
  for (const w of CWINS) if (hash(w.id, Math.floor(frac(t / LOOP + w.ph) * w.n), 17) < 0.5) { R(w.x, w.y, w.w, w.h, WARMW[1]); R(w.x, w.y, w.w, 1, WARMW[0]); }
  drawSmoke(t, wind);
  ctx.drawImage(DRUMS_C, 0, 0);
  // the west-side tenements, their windows, the café's glow
  ctx.drawImage(TOWN_C, 0, 0);
  for (const w of WINS) if (hash(w.id, Math.floor(frac(t / LOOP + w.ph) * w.n), 13) < 0.55) { R(w.x, w.y, w.w, w.h, w.on); R(w.x, w.y, w.w, 1, w.top); }
  drawCafeInside(t);
  drawTVs(t);
  halo(HL.cafe, 12, 194, 0.9); halo(HL.cafe, 46, 194, 0.9);
  ctx.drawImage(SNOWL_C, 0, 0);
  if (billboard) drawBillboard(t, pitch);
  // Sigismund's Column, floodlit
  halo(HL.shaft, COL.x - 2, 120, 0.9); halo(HL.statue, COL.x, 50, 0.9);
  ctx.drawImage(COLUMN_C, 0, 0);
  if (wx.snow) drawSnow(1, t, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // light laid on the wet stone, then everyone on the square in feet order
  for (const [sx, w, col, a, y0] of STREAKS) streak(ctx, t, sx, w, col, a, sx * 0.37, y0, H);
  const door = WAITER_T.reduce((m, w) => Math.max(m, env(t, w, w + 12, 1.2)), 0);
  if (door > 0) { ctx.globalAlpha = 0.35 * door; for (let y = 209; y < 236; y++) R(24 - ((y - 209) >> 2), y, 8 + ((y - 209) >> 1), 1, '#ffb868'); ctx.globalAlpha = 1; halo(HL.cafe, 28, 204, 0.5 * door); }
  drawPeople(t, crowd);
  halo(HL.heater, HEATER.x + 1, HEATER.top + 5, 0.8 + 0.2 * hash(4, step(t, 1440), 2));
  for (let k = 0; k < 14; k++) {
    const bx = PLANTER.x0 + 2 + Math.floor(hash(k, 1, 91) * 32), by = PLANTER.top - 3 - Math.floor(hash(k, 2, 91) * 18), on = 0.6 + 0.4 * wave(t, 24 + (k % 5) * 6, k * 0.37);
    R(bx, by, 1, 1, on > 0.8 ? '#fff2c0' : '#ffc860'); halo(HL.bulb, bx, by, on * 0.7);
  }
  for (const [x, y] of [[LAMP.x, LAMP.y - 4], [LAMP.x - 12, LAMP.y + 14], [LAMP.x + 12, LAMP.y + 14]]) { halo(HL.lamp, x, y + 2, 0.7); halo(HL.lampCore, x, y + 2, 0.8); }
  if (wx.snow) drawSnow(2, t, lights);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

// a lit window of the green house, for the tap spot (the same draw as drawTenement's)
function litWindowSpot() {
  const T = TEN[2], ground = T.base - Math.round(T.st * 1.55), wh = Math.round(T.st * 0.58);
  for (let y = T.cor + Math.round(T.st * 0.3), s = 0; y + T.st * 0.6 < ground; y += T.st, s++)
    for (const ax of T.axes) if (hash(T.seed, s, ax) < 0.42) return { kind: 'window', x: ax - 1, y: y - 1, w: T.ww + 2, h: wh + 2 };
  return { kind: 'window', x: T.axes[1] - 1, y: 130, w: T.ww + 2, h: wh + 2 };
}

const CROWD = 0.5;
export default {
  id: 'warsaw',
  name: 'Warsaw',
  tagline: 'First snow on Castle Square',
  timeZone: 'Europe/Warsaw',
  accent: '#8a1538',
  focusX: 252,
  landmark: [COL.x - 21, CT.x1 + 4],                 // Sigismund's Column's steps to the Clock Tower's shaded side
  landmarkTop: COL.tip,                              // the king's cross
  billboard: BB,
  crowd: CROWD,
  spots: [                                           // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: COL.x - 11, y: COL.tip, w: 23, h: COL.ground - COL.tip + 1 },   // Sigismund's Column
    { kind: 'bell', x: CT.x0 - 2, y: CT.tip, w: CT.x1 - CT.x0 + 7, h: CT.base - CT.tip },    // the Clock Tower
    litWindowSpot(),                                                                       // a lit window of the green house
  ],
  ambience: { wind: 0.22, crowd: 0.16, traffic: 0.05 },
  events: [
    { t: 56, type: 'chime', x: 313, gain: 0.5 }, ...[60, 63, 66, 69].map(t => ({ t, type: 'bell', x: 313 })),
    { t: 104, type: 'chime', x: 320, gain: 0.35 }, { t: 176, type: 'chime', x: 313, gain: 0.5 },
    ...[180, 183, 186, 189].map(t => ({ t, type: 'bell', x: 313 })), { t: 190, type: 'chime', x: 230, gain: 0.35 },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
